import React, { useState, useEffect, useCallback } from 'react';
import { showSuccess, showError, showWarning, showInfo } from '../utils/toast';
import LoadingSpinner from './LoadingSpinner';
import Button from './ui/Button';
import AnalysisFieldEditor from './AnalysisFieldEditor';
import PreviewReanaliseModal from './PreviewReanaliseModal'; // #204
import { ncService } from '../services/api';
import authService from '../services/authService'; // #271
import { diagnosticarPilarPrincipal } from '../utils/pilarCoerencia';
import { buildPorquesEdicoes, buildPorquesEdicoesCadeiaCompleta } from '../utils/porquesDiff'; // #91 / #219
import { formatHipoteseAlternativaPorque } from '../utils/porqueFormat';
import { normalizarAcaoCorretiva } from '../utils/acoesAdapter'; // #219 — leitura robusta das ações
import { parseCurrencyBR, formatCurrencyBR } from '../utils/currency';
import { DISPOSICAO_OPCOES } from '../utils/iso87'; // #279/#280 — ISO 8.7
import API_URL from '../config';
import './NCEditor.css';
import { recordDraft, clearDraft, peekPersistedDraft, discardPersistedDraft } from '../utils/ncDraftStore'; // #145

// Normaliza analise.cinco_porques ([str] legacy OU [{numero,pergunta,resposta}]
// v4 pós shape-migration) pro shape do editor: array de STRINGS com mínimo 5
// slots, preservando N>5 (#91 — porquês 6-10 não podem ser truncados).
function normalizarPorquesEditor(cincoPorques) {
  const existing = Array.isArray(cincoPorques) ? cincoPorques : [];
  const respostas = existing.map(p =>
    typeof p === 'string' ? p : (p?.resposta || '')
  );
  while (respostas.length < 5) respostas.push('');
  return respostas;
}

// #70 (fluxo 8D) — locais válidos de uma frente de contenção (espelha o
// enum do backend: validar_frentes_contencao / LOCAIS_FRENTE_CONTENCAO).
const LOCAIS_FRENTE = [
  ['estoque', 'Estoque'],
  ['em_transito', 'Em trânsito'],
  ['no_cliente', 'No cliente'],
  ['linha_producao', 'Linha de produção'],
  ['outro', 'Outro'],
];
const LOCAL_FRENTE_LABEL = Object.fromEntries(LOCAIS_FRENTE);

// % NOK computado no read (nada armazenado) — mesma conta dos relatórios.
function pctNok(total, nok) {
  const t = Number(total); const n = Number(nok);
  if (!Number.isFinite(t) || !Number.isFinite(n) || t <= 0) return null;
  return `${((n / t) * 100).toFixed(1)}%`;
}

// #70 — editor das FRENTES de contenção estruturada (Estoque/Trânsito/
// Cliente/Linha — qtd total × NOK). Grava a LISTA INTEIRA num path único
// (acao_contencao_imediata.frentes) — sem trilha-por-índice (lição #189).
// O backend re-valida/normaliza (422 amigável em local/quantidade inválida).
function FrentesContencaoEditor({ frentes, onSave, disabled }) {
  const [rows, setRows] = useState(null); // null = modo leitura
  const [salvando, setSalvando] = useState(false);
  const lista = Array.isArray(frentes) ? frentes : [];

  const linhaVazia = { local: 'estoque', descricao: '', qtd_total: '', qtd_nok: '' };
  const iniciarEdicao = () => setRows(
    lista.length
      ? lista.map(f => ({
          local: f.local || 'estoque',
          descricao: f.descricao || '',
          qtd_total: f.qtd_total ?? '',
          qtd_nok: f.qtd_nok ?? '',
        }))
      : [{ ...linhaVazia }]
  );
  const setCampo = (i, k) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, [k]: e.target.value } : r)));
  const addRow = () => setRows(rs => (rs.length < 10 ? [...rs, { ...linhaVazia }] : rs));
  const rmRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const salvar = async () => {
    const listaOut = rows
      .filter(r => String(r.descricao).trim() || String(r.qtd_total).trim() !== '' || String(r.qtd_nok).trim() !== '')
      .map(r => {
        const item = { local: r.local };
        if (String(r.descricao).trim()) item.descricao = String(r.descricao).trim();
        if (String(r.qtd_total).trim() !== '') item.qtd_total = r.qtd_total;
        if (String(r.qtd_nok).trim() !== '') item.qtd_nok = r.qtd_nok;
        return item;
      });
    setSalvando(true);
    try {
      await onSave(listaOut);
      setRows(null);
    } catch {
      // handleSaveFieldV4 já mostrou o toast de erro — mantém a edição aberta
    } finally {
      setSalvando(false);
    }
  };

  const thStyle = { textAlign: 'left', fontSize: '12px', color: '#64748b', padding: '4px 6px' };
  const tdStyle = { padding: '3px 6px' };

  return (
    <div className="frentes-contencao" style={{ marginTop: '14px', borderTop: '1px dashed #e2e8f0', paddingTop: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>
          Frentes de contenção (onde o material suspeito foi verificado)
        </label>
        {rows === null && (
          <Button type="button" variant="secondary" size="sm" icon="edit" onClick={iniciarEdicao} disabled={disabled}>
            {lista.length ? 'Editar frentes' : 'Adicionar frentes'}
          </Button>
        )}
      </div>

      {rows === null ? (
        lista.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Frente</th>
                <th style={thStyle}>Descrição</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Qtd. inspecionada</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Qtd. NOK</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>% NOK</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((f, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={tdStyle}>{LOCAL_FRENTE_LABEL[f.local] || f.local}</td>
                  <td style={tdStyle}>{f.descricao || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{f.qtd_total ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{f.qtd_nok ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{pctNok(f.qtd_total, f.qtd_nok) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : (
        <div style={{ marginTop: '8px' }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px 90px 60px 28px', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
              <select value={r.local} onChange={setCampo(i, 'local')} disabled={salvando}>
                {LOCAIS_FRENTE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input type="text" value={r.descricao} onChange={setCampo(i, 'descricao')} maxLength={500} placeholder="Descrição (ex: bloqueio do lote no CD)" disabled={salvando} />
              <input type="number" min="0" value={r.qtd_total} onChange={setCampo(i, 'qtd_total')} placeholder="Qtd. total" disabled={salvando} />
              <input type="number" min="0" value={r.qtd_nok} onChange={setCampo(i, 'qtd_nok')} placeholder="NOK" disabled={salvando} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>{pctNok(r.qtd_total, r.qtd_nok) || '—'}</span>
              <button type="button" onClick={() => rmRow(i)} disabled={salvando} title="Remover frente"
                style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            {rows.length < 10 && (
              <Button type="button" variant="secondary" size="sm" icon="add" onClick={addRow} disabled={salvando}>
                Frente
              </Button>
            )}
            <Button type="button" variant="primary" size="sm" icon="save" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar frentes'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setRows(null)} disabled={salvando}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// #69 (fluxo 8D) — dimensões da abrangência (espelha o enum do backend:
// validar_abrangencia / DIMENSOES_ABRANGENCIA). D2: onde o problema pode
// ter se espalhado além do caso reportado.
const DIMENSOES_ABRANGENCIA = [
  ['outros_lotes', 'Outros lotes'],
  ['outros_produtos', 'Outros produtos'],
  ['processos_similares', 'Processos similares'],
  ['estoque', 'Estoque'],
  ['em_transito', 'Em trânsito'],
  ['no_cliente', 'No cliente'],
  ['outro', 'Outro'],
];
const DIMENSAO_LABEL = Object.fromEntries(DIMENSOES_ABRANGENCIA);

// #69 — editor da ABRANGÊNCIA multi-dimensional. Checklist das 7 dimensões:
// marcar "verificado" + resultado por dimensão. Grava a LISTA INTEIRA num
// path único ('abrangencia') — mesmo padrão das frentes (#70, lição #189).
// Estende o MVP #280 (o checkbox geral NC.abrangencia_verificada segue
// na seção Disposição como selo).
function AbrangenciaEditor({ abrangencia, onSave, disabled }) {
  const [rows, setRows] = useState(null); // null = modo leitura
  const [salvando, setSalvando] = useState(false);
  const lista = Array.isArray(abrangencia) ? abrangencia : [];

  const iniciarEdicao = () => {
    // Semeia TODAS as dimensões (as já salvas mantêm estado; as demais em branco)
    const porDim = Object.fromEntries(lista.map(a => [a.dimensao, a]));
    setRows(DIMENSOES_ABRANGENCIA.map(([dim]) => ({
      dimensao: dim,
      verificado: !!porDim[dim]?.verificado,
      resultado: porDim[dim]?.resultado || '',
      _presente: !!porDim[dim],
    })));
  };
  const setVerificado = (i) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, verificado: e.target.checked, _presente: true } : r)));
  const setResultado = (i) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, resultado: e.target.value, _presente: true } : r)));

  const salvar = async () => {
    // Só entram na lista as dimensões TOCADAS (verificado ou resultado) —
    // o backend dropa vazios de qualquer forma.
    const listaOut = rows
      .filter(r => r._presente && (r.verificado || String(r.resultado).trim()))
      .map(r => {
        const item = { dimensao: r.dimensao, verificado: !!r.verificado };
        if (String(r.resultado).trim()) item.resultado = String(r.resultado).trim();
        return item;
      });
    setSalvando(true);
    try {
      await onSave(listaOut);
      setRows(null);
    } catch {
      // toast de erro já mostrado pelo handleSaveFieldV4 — mantém a edição
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="abrangencia-editor">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          Onde o problema FOI e NÃO FOI encontrado — verifique cada dimensão
          aplicável (D2 do 8D).
        </p>
        {rows === null && (
          <Button type="button" variant="secondary" size="sm" icon="edit" onClick={iniciarEdicao} disabled={disabled}>
            {lista.length ? 'Editar' : 'Preencher'}
          </Button>
        )}
      </div>

      {rows === null ? (
        lista.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            {lista.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '4px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', fontSize: '13px' }}>
                <span style={{ fontWeight: 600, color: a.verificado ? '#166534' : '#92400e' }}>
                  {a.verificado ? '✔' : '○'}
                </span>
                <span style={{ minWidth: '150px', fontWeight: 500 }}>{DIMENSAO_LABEL[a.dimensao] || a.dimensao}</span>
                <span style={{ color: '#64748b' }}>{a.resultado || (a.verificado ? 'Verificado' : 'A verificar')}</span>
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={{ marginTop: '8px' }}>
          {rows.map((r, i) => (
            <div key={r.dimensao} style={{ display: 'grid', gridTemplateColumns: '24px 160px 1fr', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              <input type="checkbox" checked={r.verificado} onChange={setVerificado(i)} disabled={salvando} title="Verificado" />
              <label style={{ fontSize: '13px', fontWeight: 500 }}>{DIMENSAO_LABEL[r.dimensao]}</label>
              <input type="text" value={r.resultado} onChange={setResultado(i)} maxLength={500}
                placeholder="Resultado (ex: 3 lotes inspecionados, 0 NOK)" disabled={salvando} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <Button type="button" variant="primary" size="sm" icon="save" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar abrangência'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setRows(null)} disabled={salvando}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// #242 (épico automotivo) — TRIAGEM 6M (verde/vermelho). Overlay MANUAL que
// prioriza os pilares do Ishikawa como suspeita × a-testar. v1 SÓ suspeita
// (sem "excluída-com-evidência" — fast-follow gated). A IA NÃO gera.
const PILARES_6M_TRIAGEM = [
  ['mao_de_obra', 'Mão de obra'],
  ['metodo', 'Método'],
  ['maquina', 'Máquina'],
  ['material', 'Material'],
  ['medicao', 'Medição'],
  ['meio_ambiente', 'Meio ambiente'],
];
const PILAR_6M_LABEL = Object.fromEntries(PILARES_6M_TRIAGEM);
const TRIAGEM_STATUS_OPCOES = [
  ['', '—'],
  ['suspeita', 'Suspeita'],
  ['a_testar', 'A testar'],
];
const triagemCor = (s) => (s === 'suspeita' ? '#b91c1c' : s === 'a_testar' ? '#b45309' : '#94a3b8');
const triagemMarca = (s) => (s === 'suspeita' ? '🔴' : s === 'a_testar' ? '🟡' : '○');
const triagemRotulo = (s) => (s === 'suspeita' ? 'Suspeita' : s === 'a_testar' ? 'A testar' : '');

// Grava a LISTA INTEIRA num path único ('triagem_6m') — padrão frentes/abrangência
// (lição #189, sem índice). validar_triagem_6m() valida no backend.
function Triagem6MEditor({ triagem, onSave, disabled }) {
  const [rows, setRows] = useState(null); // null = modo leitura
  const [salvando, setSalvando] = useState(false);
  const lista = Array.isArray(triagem) ? triagem : [];

  const iniciarEdicao = () => {
    const porPilar = Object.fromEntries(lista.map(t => [t.pilar, t]));
    setRows(PILARES_6M_TRIAGEM.map(([p]) => ({
      pilar: p,
      status: porPilar[p]?.status || '',
      nota: porPilar[p]?.nota || '',
    })));
  };
  const setStatus = (i) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, status: e.target.value } : r)));
  const setNota = (i) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, nota: e.target.value } : r)));

  const salvar = async () => {
    const listaOut = rows
      .filter(r => r.status)  // só pilares com status marcado
      .map(r => {
        const item = { pilar: r.pilar, status: r.status };
        if (String(r.nota).trim()) item.nota = String(r.nota).trim();
        return item;
      });
    setSalvando(true);
    try {
      await onSave(listaOut);
      setRows(null);
    } catch {
      // toast de erro já mostrado por handleSaveFieldV4 — mantém a edição
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="triagem-6m-editor">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          Priorize os pilares: marque os que são <strong>suspeita</strong> (perseguir) ou
          <strong> a testar</strong> (ainda não examinado). Marcação manual — orienta a investigação.
        </p>
        {rows === null && (
          <Button type="button" variant="secondary" size="sm" icon="edit" onClick={iniciarEdicao} disabled={disabled}>
            {lista.length ? 'Editar' : 'Preencher'}
          </Button>
        )}
      </div>

      {rows === null ? (
        lista.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            {lista.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '4px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', fontSize: '13px' }}>
                <span>{triagemMarca(t.status)}</span>
                <span style={{ minWidth: '140px', fontWeight: 500 }}>{PILAR_6M_LABEL[t.pilar] || t.pilar}</span>
                <span style={{ color: triagemCor(t.status), fontWeight: 500 }}>{triagemRotulo(t.status)}</span>
                {t.nota && <span style={{ color: '#64748b' }}>· {t.nota}</span>}
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={{ marginTop: '8px' }}>
          {rows.map((r, i) => (
            <div key={r.pilar} style={{ display: 'grid', gridTemplateColumns: '140px 120px 1fr', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500 }}>{PILAR_6M_LABEL[r.pilar]}</label>
              <select value={r.status} onChange={setStatus(i)} disabled={salvando}>
                {TRIAGEM_STATUS_OPCOES.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
              </select>
              <input type="text" value={r.nota} onChange={setNota(i)} maxLength={500}
                placeholder="Nota (opcional — ex: sensor descalibrado)" disabled={salvando} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <Button type="button" variant="primary" size="sm" icon="save" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar triagem'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setRows(null)} disabled={salvando}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// #241 (épico automotivo) — cadeia de DETECÇÃO ("por que NÃO foi detectado?",
// D4 do 8D). A cadeia de porquês EXISTENTE é a vertente OCORRÊNCIA — rótulo,
// nunca campo novo (decisão 08/07: duas fontes pra mesma verdade divergem).
// Só a detecção nasce nova. Grava a LISTA INTEIRA num path único
// ('porques_deteccao') — padrão triagem/abrangência (lição #189, sem índice);
// validar_porques_deteccao() valida no backend. A IA NÃO gera no v1.
function PorquesDeteccaoEditor({ porques, onSave, disabled }) {
  const [rows, setRows] = useState(null); // null = modo leitura
  const [salvando, setSalvando] = useState(false);
  const lista = Array.isArray(porques) ? porques : [];

  const iniciarEdicao = () => {
    const base = lista.map(p => ({ resposta: p?.resposta || '' }));
    setRows(base.length ? base : [{ resposta: '' }]);
  };
  const setResposta = (i) => (e) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, resposta: e.target.value } : r)));
  const adicionarRow = () => setRows(rs => [...rs, { resposta: '' }]);

  const salvar = async () => {
    const listaOut = rows
      .map(r => String(r.resposta || '').trim())
      .filter(Boolean)
      .map((resposta, i) => ({ numero: i + 1, resposta }));
    setSalvando(true);
    try {
      await onSave(listaOut);
      setRows(null);
    } catch {
      // toast de erro já mostrado por handleSaveFieldV4 — mantém a edição
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="porques-deteccao-editor">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          Por que o problema <strong>não foi detectado</strong> pelos controles antes de chegar ao
          cliente (D4 do 8D)? Cadeia manual — a de porquês acima responde "por que ocorreu".
        </p>
        {rows === null && (
          <Button type="button" variant="secondary" size="sm" icon="edit" onClick={iniciarEdicao} disabled={disabled}>
            {lista.length ? 'Editar' : 'Preencher'}
          </Button>
        )}
      </div>

      {rows === null ? (
        lista.length > 0 && (
          <ol style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '13px' }}>
            {lista.map((p, i) => (
              <li key={i} style={{ marginBottom: '4px' }}>{p?.resposta || ''}</li>
            ))}
          </ol>
        )
      ) : (
        <div style={{ marginTop: '8px' }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '8px', alignItems: 'start', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, paddingTop: '6px' }}>Porquê {i + 1}</label>
              <textarea value={r.resposta} onChange={setResposta(i)} rows={2} maxLength={2000}
                placeholder="Ex: a inspeção final não cobria essa dimensão…" disabled={salvando} />
            </div>
          ))}
          {rows.length < 10 && (
            <Button type="button" variant="secondary" size="sm" icon="add" onClick={adicionarRow} disabled={salvando}>
              Adicionar porquê
            </Button>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <Button type="button" variant="primary" size="sm" icon="save" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar cadeia de detecção'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setRows(null)} disabled={salvando}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: fetch com token JWT automático
function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// PR-B avulsa — injeção aditiva (default = comportamento do app, byte-idêntico):
//  • editarAnaliseFn: função de persistência da edição. Default ncService.editarAnalise
//    (app). O fluxo avulsa injeta avulsaService.editarAnalise → mesmo editor, outro
//    endpoint/token (o comprador é admin do tenant dono da NC).
//  • allowIaRecalc: gate dos botões/checagens com IA (6M / porquês / Validar
//    Consistência). Default true (app). Avulsa passa false → recálculo com IA no
//    avulsa é o "rerun" (re-roda a Investigação inteira, consome 1 do pool).
//  • allowBaseFieldEdit: gate da edição dos campos BASE da NC (contexto/indústria/
//    custo/impacto/observações) — esses salvam via endpoints do app (token do app,
//    sem equivalente avulsa). Default true (app). Avulsa passa false: edita a
//    ANÁLISE (causa/porquês/ações/6M/…) livremente; os inputs ficam read-only
//    (mudar o contexto no avulsa é o "rerun", que re-roda a análise inteira).
function NCEditor({ ncId, analise, analiseV4, nc, onSave, onUnsavedChangesChange, editarAnaliseFn = ncService.editarAnalise, allowIaRecalc = true, allowBaseFieldEdit = true }) {
  // #219 Fase 2 — fim do botão mestre "Editar Análise"/"Salvar". O editor é a
  // superfície única em rascunho; as 3 seções LEGADAS (porquês, causa, ações)
  // ganham lápis PRÓPRIO com modo edição + Salvar/Cancelar por seção. As seções
  // v4 (AnalysisFieldEditor) ficam com lápis-por-campo sempre destravado.
  const [porquesEdit, setPorquesEdit] = useState(false);
  const [causaEdit, setCausaEdit] = useState(false);
  const [acoesEdit, setAcoesEdit] = useState(false);
  // Dirty POR SEÇÃO (substitui o hasUnsavedChanges único): salvar uma seção não
  // pode "limpar" edições pendentes de outra (guard + draft #145 + re-sync TD-146).
  const [porquesDirty, setPorquesDirty] = useState(false);
  const [causaDirty, setCausaDirty] = useState(false);
  const [acoesDirty, setAcoesDirty] = useState(false);
  // Lápis individuais (AnalysisFieldEditor) abertos — alimenta o guard de
  // navegação. Set por `campo` = idempotente (imune a sinal duplicado).
  const [camposAbertos, setCamposAbertos] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  // #204: prévia das reanálises parciais em modal (substitui o window.confirm).
  // Guarda tudo que o aceite precisa (edicoes + campos_reanalisados do preview).
  const [previewReanalise, setPreviewReanalise] = useState(null);
  const [aceitandoPreview, setAceitandoPreview] = useState(false);
  // Perf/UX (#193 I-B-adj): mensagem do overlay de loading, específica por
  // operação (ex.: recalcular IA ~30s). Default 'Processando...'; os handlers
  // de recalc setam a sua e resetam no finally.
  const [loadingMessage, setLoadingMessage] = useState('Processando...');
  // #219 follow-up — o toggle "Editar livremente (sem IA)" foi APOSENTADO: o
  // save já é SEMPRE limpo (sem IA) por padrão desde a B1/Etapa-1, então o
  // toggle era redundante e enganoso (sugeria que o modo normal usava IA). A
  // const fica fixa em false pra os saves seguirem enviando `override: false`
  // — exatamente o caminho do usuário que nunca clicava o toggle.
  const overrideMode = false;
  // Investigação (#193 I-A): 6M expandido por padrão — é o 1º passo da spine
  // (6M→porquês→causa→ações). #219 fix: Contenção também expandida — é a
  // resposta imediata (8D), curta e importante, e era SEMPRE visível antes da
  // Fase 2 (via read-only). Sem isto ela ficava escondida em rascunho. As
  // demais seções v4 (conclusão/impacto/hipótese/5W2H/preventivas/eficácia)
  // seguem recolhidas (secundárias/derivadas).
  const [expandedSections, setExpandedSections] = useState({ '6m': true, 'contencao': true });

  // Sessão 41 — tier Registro: não edita os campos exclusivos da Investigação
  // (6M, hipótese, impacto, 5W2H, preventivas, eficácia). Render-layer only;
  // o JSON/banco não muda.
  const isRegistro = (nc?.modo_analise || 'registro') === 'registro';

  // #77 Felipe (Sessão 27) — campo Observações livres do usuário.
  // Estado local independente do editedData porque o save é dedicado
  // (PATCH /observacoes não passa pelo /editar-analise nem propaga IA).
  const [observacoes, setObservacoes] = useState(nc?.observacoes || '');
  const [observacoesOriginal, setObservacoesOriginal] = useState(nc?.observacoes || '');
  const [savingObservacoes, setSavingObservacoes] = useState(false);

  // Re-sincroniza estado local quando a NC é recarregada (refetch após save
  // em outro campo, cascata IA, restore). Espelha o padrão dos outros
  // useEffect deste componente.
  useEffect(() => {
    setObservacoes(nc?.observacoes || '');
    setObservacoesOriginal(nc?.observacoes || '');
  }, [nc?.observacoes]);
  // Buffer das seções legadas (porquês/causa/ações). O shape mantém
  // `acao_imediata` por compat com rascunhos #145 persistidos pré-Fase-2 —
  // o campo não tem mais UI própria (a Contenção edita o mesmo dado).
  const [editedData, setEditedData] = useState({
    causa_raiz: analise?.causa_raiz || '',
    cinco_porques: normalizarPorquesEditor(analise?.cinco_porques), // #91: min 5 slots, preserva N>5
    acao_imediata: analise?.acao_imediata || '',
    acoes_corretivas: analise?.acoes_corretivas || [],
  });
  const [porqueEditado, setPorqueEditado] = useState(() => Array(Math.max(5, analise?.cinco_porques?.length || 0)).fill(false)); // #91: dimensiona pra N porquês
  const [validacaoConsistencia, setValidacaoConsistencia] = useState(null);
  const [rascunhoRecuperavel, setRascunhoRecuperavel] = useState(null); // #145 rascunho preservado de sessão expirada

  // #219 Fase 2 — derivados do dirty por seção. `hasUnsavedChanges` guarda o
  // conteúdo digitado e não salvo (draft #145 + re-sync TD-146);
  // `algumaEdicaoAberta` soma os lápis individuais abertos (guard de navegação).
  const hasUnsavedChanges = porquesDirty || causaDirty || acoesDirty;
  const algumaEdicaoAberta = hasUnsavedChanges || camposAbertos.size > 0;

  // Callback estável pro AnalysisFieldEditor reportar abrir/fechar do lápis.
  // Guard de no-op (retorna prev) evita loop de re-render quando o sinal repete.
  const onFieldEditingChange = useCallback((campo, aberto) => {
    setCamposAbertos(prev => {
      if (prev.has(campo) === aberto) return prev;
      const next = new Set(prev);
      if (aberto) next.add(campo); else next.delete(campo);
      return next;
    });
  }, []);

  // Sessão 24 #59 — Autocomplete de emails pro campo responsavel_email.
  // Estratégia: fetch destinatarios_alertas da conta + coleta emails já
  // preenchidos em ações da NC atual. Junta tudo, deduplica, oferece como
  // <datalist> (autocomplete nativo do browser, sem libs).
  const [contaDestinatarios, setContaDestinatarios] = useState([]);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/account/destinatarios-alertas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelado) setContaDestinatarios(data.destinatarios || []);
      } catch (e) {}
    })();
    return () => { cancelado = true; };
  }, []);

  // TD-146: re-sincronizar state local quando prop `analise` muda (após
  // reanálise IA ou outro refresh do parent via onSave→onRefresh→
  // handleRefreshNC→getNC→setResult). Antes deste hook, `editedData` era
  // inicializado UMA VEZ no mount via useState(initialValue) e nunca
  // atualizava — NCEditor ficava cego a mudanças de prop. Sintoma cravado
  // por Francis: aceitar reanálise IA → state local stale → ao clicar
  // "Salvar" o frontend enviava editedData antigo → sobrescrevia a
  // reanálise no backend.
  //
  // ESCOPO RESTRITO (Sessão 12.9 decisão produto): re-sincronizar apenas
  // `causa_raiz` e `cinco_porques` — campos cobertos pela reanálise
  // parcial 6M no backend (ai_service.reanalisar_parcial_6m retorna
  // porques+causa_raiz+hipotese_alternativa, NÃO acoes_corretivas/plano).
  // `acoes_corretivas` e `acao_imediata` NÃO re-sincronizam aqui pra
  // não sobrescrever edições humanas em campos que a reanálise não
  // promete atualizar. Cadeia progressiva (descrição→6M→Porquês→causa
  // raiz→ações) fica como decisão de produto pós-demo (TD-followup
  // cravado Sessão 12.5/12.6).
  //
  // GUARD por seção (#219 Fase 2): só re-sincroniza a fatia cuja seção NÃO
  // está suja nem em edição — protege a digitação em curso contra race com
  // refresh do parent, sem que salvar UMA seção libere a sobrescrita da OUTRA
  // (o guard único antigo tinha esse furo latente: um save de campo v4
  // resetava a flag global e o re-sync engolia porquês não salvos).
  useEffect(() => {
    setEditedData((prev) => {
      const next = { ...prev };
      if (!causaDirty && !causaEdit) {
        next.causa_raiz = analise?.causa_raiz || '';
      }
      if (!porquesDirty && !porquesEdit) {
        next.cinco_porques = normalizarPorquesEditor(analise?.cinco_porques);
      }
      // acoes_corretivas e acao_imediata NÃO re-sincronizam (escopo restrito
      // Sessão 12.9); o buffer das ações é re-semeado ao abrir o lápis da seção.
      return next;
    });
  }, [analise, causaDirty, causaEdit, porquesDirty, porquesEdit]);

  // Sessão 20 — TD-followup-prompt-unsaved-changes.
  //
  // Avisa o usuário quando tenta fechar a aba / dar refresh / sair do site
  // com edições não salvas no NCEditor. Sem isso, o usuário perde
  // edições silenciosamente — caso real reportado durante demo Cumeeira.
  //
  // Limitação conhecida: `beforeunload` cobre apenas eventos do BROWSER
  // (refresh, fechar tab, navegar pra URL externa). NÃO cobre navegação
  // SPA interna (ex: clicar "Voltar à lista" no sidebar) — App.jsx usa
  // máquina de estado com `view`, sem React Router. Cobertura SPA fica
  // como TD-followup-prompt-unsaved-changes-spa.
  //
  // O texto da mensagem é controlado pelo browser (Chrome/Firefox
  // mostram texto genérico desde 2017 por segurança); só importa que
  // `event.returnValue` seja seteado pra ativar o prompt nativo.
  useEffect(() => {
    if (!algumaEdicaoAberta) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Browsers modernos ignoram a string — mas precisa seter algo
      // pro prompt aparecer. Fallback pra browsers antigos.
      e.returnValue = 'Você tem alterações não salvas. Tem certeza?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [algumaEdicaoAberta]);

  // TD-followup-prompt-unsaved-changes-spa (Sessão 21): expõe a flag pro
  // App.jsx via callback. beforeunload acima cobre fechar tab/refresh, mas
  // não navegação SPA interna (App.jsx é state machine sem React Router,
  // setView programático não dispara beforeunload). Parent guarda a flag
  // e wrapper safeSetView pergunta antes de navegar quando dirty.
  //
  // Cleanup essencial: ao desmontar (ex: usuário trocou de view),
  // sinaliza "limpo" pro parent — sem isso, flag ficaria stale após sair.
  // #219 Fase 2: o guard SPA passa a cobrir também os lápis individuais
  // abertos (algumaEdicaoAberta), não só o buffer das seções legadas.
  // (O onEditingChange da Fase 1 foi aposentado: o pai não renderiza mais o
  // bloco read-only em rascunho — o editor É a superfície única.)
  useEffect(() => {
    if (typeof onUnsavedChangesChange === 'function') {
      onUnsavedChangesChange(algumaEdicaoAberta);
    }
    return () => {
      if (typeof onUnsavedChangesChange === 'function') {
        onUnsavedChangesChange(false);
      }
    };
  }, [algumaEdicaoAberta, onUnsavedChangesChange]);

  // #145 — registra o rascunho em memória sempre que há edição não salva,
  // para que os interceptors de auth (api.js / fetchInterceptor) possam
  // persisti-lo ANTES de um logout forçado por sessão expirada. Limpa quando
  // não há mais pendência (ex.: após salvar/cancelar).
  useEffect(() => {
    if (hasUnsavedChanges) recordDraft(ncId, editedData);
    else clearDraft();
  }, [hasUnsavedChanges, editedData, ncId]);

  // #145 — ao abrir a NC, se um rascunho foi preservado numa sessão que
  // expirou, oferece restaurá-lo (banner no modo de visualização).
  useEffect(() => {
    const d = peekPersistedDraft(ncId);
    if (d) setRascunhoRecuperavel(d);
  }, [ncId]);

  // #219 Fase 2 — abrir/cancelar POR SEÇÃO. Abrir re-semeia o buffer da seção
  // a partir da prop (estado fresco; o re-sync TD-146 mantém porquês/causa em
  // dia em modo leitura, mas as AÇÕES não re-sincronizam — a semeadura no
  // abrir é o que garante cards atuais).
  const abrirEdicaoPorques = () => {
    setEditedData(prev => ({ ...prev, cinco_porques: normalizarPorquesEditor(analise?.cinco_porques) }));
    setPorqueEditado(Array(Math.max(5, analise?.cinco_porques?.length || 0)).fill(false));
    setPorquesDirty(false);
    setPorquesEdit(true);
  };

  const cancelarEdicaoPorques = () => {
    setEditedData(prev => ({ ...prev, cinco_porques: normalizarPorquesEditor(analise?.cinco_porques) }));
    setPorqueEditado(Array(Math.max(5, analise?.cinco_porques?.length || 0)).fill(false));
    setPorquesDirty(false);
    setPorquesEdit(false);
  };

  const abrirEdicaoCausa = () => {
    setEditedData(prev => ({ ...prev, causa_raiz: analise?.causa_raiz || '' }));
    setCausaDirty(false);
    setCausaEdit(true);
  };

  const cancelarEdicaoCausa = () => {
    setEditedData(prev => ({ ...prev, causa_raiz: analise?.causa_raiz || '' }));
    setCausaDirty(false);
    setCausaEdit(false);
  };

  const abrirEdicaoAcoes = () => {
    setEditedData(prev => ({
      ...prev,
      // #192: normaliza acao/responsavel via adapter (Registro usa descricao/
      // responsavel_funcao; Investigação usa what/who) para os inputs mostrarem
      // o valor correto independente do shape da IA.
      acoes_corretivas: (analise?.acoes_corretivas || []).map(a => {
        const norm = normalizarAcaoCorretiva(a);
        return { ...a, acao: norm.descricao || '', responsavel: norm.responsavel || '' };
      }),
    }));
    setAcoesDirty(false);
    setAcoesEdit(true);
  };

  const cancelarEdicaoAcoes = () => {
    setEditedData(prev => ({
      ...prev,
      acoes_corretivas: (analise?.acoes_corretivas || []).map(a => {
        const norm = normalizarAcaoCorretiva(a);
        return { ...a, acao: norm.descricao || '', responsavel: norm.responsavel || '' };
      }),
    }));
    setAcoesDirty(false);
    setAcoesEdit(false);
  };

  // #145 — restaura o rascunho preservado (sessão expirada): reaplica o buffer
  // inteiro e abre as 3 seções legadas em edição (o usuário revisa e salva
  // seção a seção; Cancelar de uma seção descarta só aquela parte).
  const handleRestaurarRascunho = () => {
    if (!rascunhoRecuperavel || !rascunhoRecuperavel.editedData) return;
    setEditedData(rascunhoRecuperavel.editedData);
    setPorquesDirty(true);
    setCausaDirty(true);
    setAcoesDirty(true);
    setPorquesEdit(true);
    setCausaEdit(true);
    setAcoesEdit(true);
    discardPersistedDraft();
    setRascunhoRecuperavel(null);
  };

  const handleDescartarRascunho = () => {
    discardPersistedDraft();
    setRascunhoRecuperavel(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
    if (field === 'causa_raiz') setCausaDirty(true);
  };

  // #219 Fase 2 — Salvar da seção PORQUÊS. Mesmo diff anti-fantasma de sempre
  // (buildPorquesEdicoes, #91) → save LIMPO via /editar-analise (sem IA).
  const handleSalvarPorques = async () => {
    const edicoes = buildPorquesEdicoes(editedData.cinco_porques, analise?.cinco_porques);
    if (edicoes.length === 0) {
      // Nada mudou de fato — só fecha a edição.
      cancelarEdicaoPorques();
      return;
    }
    setLoading(true);
    try {
      await editarAnaliseFn(ncId, edicoes, { override: overrideMode });
      setPorquesDirty(false);
      setPorquesEdit(false);
      discardPersistedDraft(); // #145 — conteúdo persistido; rascunho preservado vira obsoleto
      showSuccess('Porquês salvos!');
      if (onSave) onSave(null);
    } catch (error) {
      console.error('Erro ao salvar porquês:', error);
      showError('Erro ao salvar os porquês');
    } finally {
      setLoading(false);
    }
  };

  // #219 Fase 2 — Salvar da seção CAUSA RAIZ. Fase B1 (Filosofia C, #192)
  // preservada: grava limpo via /editar-analise, sem IA, e SÓ envia quando
  // mudou de fato (backend marca todo campo enviado como "editado humano").
  const handleSalvarCausa = async () => {
    const causaRaizAlterada = editedData.causa_raiz !== analise?.causa_raiz;
    if (!causaRaizAlterada) {
      cancelarEdicaoCausa();
      return;
    }
    setLoading(true);
    try {
      await editarAnaliseFn(ncId, [{
        campo: 'causa_raiz.declaracao',
        valor: editedData.causa_raiz || '',
      }], { override: overrideMode });
      setCausaDirty(false);
      setCausaEdit(false);
      discardPersistedDraft();
      showSuccess('Causa raiz salva!');
      if (onSave) onSave(null);
    } catch (error) {
      console.error('Erro ao salvar causa raiz:', error);
      showError('Erro ao salvar a causa raiz');
    } finally {
      setLoading(false);
    }
  };

  // #219 Fase 2 — Salvar da seção AÇÕES CORRETIVAS (Opção A, decisão Francis):
  // reusa o builder em lote de sempre, ESCOPADO às ações — incluindo a remoção
  // de cauda do #143 (ação removida não pode reaparecer no modal/PDF). O save
  // continua emitindo what/who/email de todas as ações (comportamento idêntico
  // ao Salvar mestre de antes); o que muda é só o GATILHO (lápis da seção).
  const handleSalvarAcoes = async () => {
    setLoading(true);
    try {
      const edicoes = [];

      if (Array.isArray(editedData.acoes_corretivas)) {
        // #192: Registro usa descricao/responsavel_funcao; Investigação usa what/who.
        const descCampo = isRegistro ? 'descricao' : 'what';
        const respCampo = isRegistro ? 'responsavel_funcao' : 'who';
        editedData.acoes_corretivas.forEach((acao, i) => {
          edicoes.push({ campo: `acoes_corretivas.${i}.${descCampo}`, valor: acao.acao || '' });
          edicoes.push({ campo: `acoes_corretivas.${i}.${respCampo}`, valor: acao.responsavel || '' });
          if (acao.status) {
            edicoes.push({ campo: `acoes_corretivas.${i}.status`, valor: acao.status });
          }
          // TD78: incluir prazo_dias se válido. Backend recalcula `when` automaticamente.
          if (typeof acao.prazo_dias === 'number' && acao.prazo_dias >= 1 && acao.prazo_dias <= 365) {
            edicoes.push({ campo: `acoes_corretivas.${i}.prazo_dias`, valor: acao.prazo_dias });
          }
          // #66 Felipe N2.3 — prazo_data (data absoluta ISO YYYY-MM-DD).
          // Quando preenchido, backend `_resolve_data_limite` dá precedência
          // sobre prazo_dias. Sempre envia pra permitir limpar (string vazia).
          if (typeof acao.prazo_data !== 'undefined') {
            edicoes.push({
              campo: `acoes_corretivas.${i}.prazo_data`,
              valor: acao.prazo_data || '',
            });
          }
          // #66 Felipe N2.3 — concluida_em (data ISO YYYY-MM-DD). Se setado,
          // cron pula D-3/D-0/D+1 (ação concluída). null = ação ativa.
          if (typeof acao.concluida_em !== 'undefined') {
            edicoes.push({
              campo: `acoes_corretivas.${i}.concluida_em`,
              valor: acao.concluida_em || '',
            });
          }
          // Sessão 24 — Felipe F1.3: email do responsável pra alertas D-3/D-0/D+1.
          // Sempre envia (string vazia limpa o campo); backend persiste só
          // se passar validação (presença de '@').
          edicoes.push({
            campo: `acoes_corretivas.${i}.responsavel_email`,
            valor: (acao.responsavel_email || '').trim().toLowerCase(),
          });
        });

        // #143 FIX: o loop acima sobrescreve os índices 0..M-1 do array v4, mas
        // o backend NUNCA encurta o array — ações REMOVIDAS (removeAcao só faz
        // .filter local) sobravam na cauda e reapareciam no modal/PDF (leitura
        // v4-first); remover do MEIO chegava a DUPLICAR ([A,B,C] del B → [A,C,C]).
        // Solução: após reescrever os sobreviventes na frente, remover o
        // excedente da cauda (índices novoLen..origLen-1) em ordem DECRESCENTE
        // (pra o pop não deslocar índices ainda não removidos). Espelha o
        // `.-{idx}` que as ações PREVENTIVAS já usam; backend suporta
        // (acoes_corretivas em _ARRAYS_EDITAVEIS). origLen = tamanho que o
        // backend tem hoje (= o que o editor carregou de `analise`).
        const origLenAcoes = Array.isArray(analise?.acoes_corretivas)
          ? analise.acoes_corretivas.length
          : 0;
        const novoLenAcoes = editedData.acoes_corretivas.length;
        for (let i = origLenAcoes - 1; i >= novoLenAcoes; i--) {
          edicoes.push({ campo: `acoes_corretivas.-${i}`, valor: null });
        }
      }

      if (edicoes.length > 0) {
        await editarAnaliseFn(ncId, edicoes, { override: overrideMode });
      }

      setAcoesDirty(false);
      setAcoesEdit(false);
      discardPersistedDraft();
      showSuccess('Ações corretivas salvas!');
      if (onSave) onSave(null);
    } catch (error) {
      console.error('Erro ao salvar ações:', error);
      showError('Erro ao salvar as ações corretivas');
    } finally {
      setLoading(false);
    }
  };

  // (#194: handler órfão handleReanalisar do /reanalisar full REMOVIDO no #219
  // Fase 2 — nenhum botão o acionava desde a Etapa-1 da simplificação.)

  const handlePorqueChange = (index, value) => {
    const newPorques = [...editedData.cinco_porques];
    newPorques[index] = value;
    setEditedData(prev => ({ ...prev, cinco_porques: newPorques }));
    setPorquesDirty(true);

    // Marcar como editado
    const newEditado = [...porqueEditado];
    newEditado[index] = true;
    setPorqueEditado(newEditado);
  };

  // Item 4 (#191) — Adicionar porquê: cresce a cadeia de porquês localmente.
  // O backend já aceita o append (cinco_porques.N.resposta), provado em
  // test_2a_cadeia_longa_porques. Slot vazio NÃO é salvo até o usuário digitar
  // (buildPorquesEdicoes ignora vazio==vazio — anti-fantasma). Cap visual em 10
  // = teto de geração da IA (RACIOCINIO_REGISTRO "entre 3 e 10 Porquês").
  const handleAdicionarPorque = () => {
    // #219 Fase 2: clicado em modo leitura → abre a seção em edição primeiro
    // (semeia o buffer da cadeia salva) e então acrescenta o slot. Os dois
    // setEditedData são processados em ordem na mesma batch do React.
    if (!porquesEdit) abrirEdicaoPorques();
    setEditedData(prev => ({
      ...prev,
      cinco_porques: [...(prev.cinco_porques || []), ''],
    }));
    setPorqueEditado(prev => [...prev, false]);
  };

  // Item 3 (Fase B2, #192) — desligar EXPLÍCITO da flag de dessincronia das
  // ações. Chama o endpoint dedicado /acoes-revisadas (não toca em ação
  // nenhuma) → refresh limpa a flag e o banner some. O sistema avisa, o
  // usuário reconhece; nada é auto-corrigido nem adivinhado.
  const handleMarcarAcoesRevisadas = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/acoes-revisadas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      showSuccess('Ações marcadas como revisadas.');
      if (onSave) onSave(null);
    } catch (err) {
      showError('Erro ao marcar ações como revisadas.');
    } finally {
      setLoading(false);
    }
  };

  const handleValidarConsistencia = async () => {
    setLoading(true);
    
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/validar-porques`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedData.cinco_porques)  // Enviar array diretamente
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.validacao) {
        setValidacaoConsistencia(data.validacao);
      } else {
        console.error('Resposta sem validação:', data);
        showWarning('Resposta inválida do servidor');
      }
      
    } catch (error) {
      console.error('Erro ao validar consistência:', error);
      showError(`Erro ao validar consistência: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAcaoChange = (index, field, value) => {
    setEditedData(prev => {
      const newAcoes = [...prev.acoes_corretivas];
      newAcoes[index] = { ...newAcoes[index], [field]: value };
      return { ...prev, acoes_corretivas: newAcoes };
    });
    setAcoesDirty(true);
  };

  const addAcao = () => {
    // #219 Fase 2: clicado em modo leitura → abre a seção (semeia o buffer)
    // antes de acrescentar. Updaters funcionais garantem a ordem na batch.
    if (!acoesEdit) abrirEdicaoAcoes();
    // TD78: default prazo_dias=30 (1 mês, padrão ISO 9001 típico)
    setEditedData(prev => ({
      ...prev,
      acoes_corretivas: [
        ...prev.acoes_corretivas,
        { acao: '', responsavel: '', responsavel_email: '', prazo_dias: 30 }
      ]
    }));
    setAcoesDirty(true);
  };

  const removeAcao = (index) => {
    setEditedData(prev => ({
      ...prev,
      acoes_corretivas: prev.acoes_corretivas.filter((_, i) => i !== index),
    }));
    setAcoesDirty(true);
  };

  // === FULL ANALYSIS OVERRIDE (v4.1) ===
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const camposEditadosHumano = analiseV4?.metadados_ia?.campos_editados_humano || [];

  const isEditadoPorHumano = (campo) => {
    return camposEditadosHumano.some(c => campo.startsWith(c) || c.startsWith(campo));
  };

  // #144: uma ação corretiva está "não editada" quando NENHUM dos seus campos
  // foi editado por humano. Check preciso COM ponto final pra não confundir o
  // índice 1 com 10/11 ('acoes_corretivas.10.what' NÃO casa 'acoes_corretivas.1.').
  // #189 P1: a trilha passou a ser gravada por ID estável da ação
  // ('acoes_corretivas.AC-03.what') — o índice deriva quando outra ação é
  // excluída/reordenada. O lookup aceita as DUAS formas: id (trilha nova) e
  // índice (entries legadas pré-#189).
  const acaoCorretivaEditada = (acao, index) =>
    camposEditadosHumano.some((c) => {
      if (typeof c !== 'string') return false;
      if (acao?.id && c.startsWith(`acoes_corretivas.${acao.id}.`)) return true;
      return c.startsWith(`acoes_corretivas.${index}.`);
    });

  // #189 P1: idem pro badge POR CAMPO das ações (corretivas v4 e preventivas) —
  // tenta o path por id da ação primeiro, cai pro path por índice (legado).
  const isCampoAcaoEditado = (arrayName, item, index, field) => {
    if (item?.id && isEditadoPorHumano(`${arrayName}.${item.id}.${field}`)) return true;
    return isEditadoPorHumano(`${arrayName}.${index}.${field}`);
  };

  const handleSaveFieldV4 = async (campo, valor) => {
    try {
      const result = await editarAnaliseFn(ncId, [{ campo, valor }], { override: overrideMode });
      if (result.status === 'success' || result.status === 'preview') {
        showSuccess('Campo atualizado!');
        // #219 Fase 2: o antigo reset "defensivo" do dirty global foi removido
        // — ele deixava o re-sync TD-146 ENGOLIR porquês/causa digitados e não
        // salvos quando um campo v4 era salvo no meio (bug latente). O dirty
        // por seção preserva os buffers das outras seções.
        // Recarregar dados da NC sem perder a rota
        if (onSave) onSave(null);
      }
    } catch (err) {
      console.error('Erro ao salvar campo v4:', err);
      const detail = err?.response?.data?.detail || err.message || 'Erro desconhecido';
      showError(`Erro ao salvar: ${detail}`);
      throw err;
    }
  };

  // Sessão 19 — Edição dos campos BASE da NC (contextos + indústria).
  // Não passa pelo endpoint /editar-analise (dot notation no JSON da
  // análise) porque esses campos estão na tabela NC, não na análise.
  // Usa PUT /api/v2/nc/{id}/editar com whitelist de campos do backend
  // (`contexto_processo`, `contexto_setorial_custom`, `industria`,
  // ver nc_routes_v2.py:164-168).
  const handleSaveContextoNC = async (campo, valor) => {
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/editar`, {
        method: 'PUT',
        body: JSON.stringify({
          campo,
          valor,
          usuario: 'usuario@empresa.com',
        }),
      });
      // #271 — sessão expirada: o fetchInterceptor global já tenta refresh; se
      // ainda chegou 401 aqui, a sessão é irrecuperável (refresh vencido/
      // revogado, ou sem refresh token). Em vez do jargão "HTTP 401", mostra a
      // mensagem humana (#215) e aciona a MESMA mecânica de encerramento dos
      // interceptors (flush do rascunho + flag + redirect pra tela de login).
      // O throw mantém o rollback do campo no AnalysisFieldEditor.
      if (response.status === 401) {
        showInfo('Sua sessão expirou — faça login novamente. O que você editou aqui não foi salvo.');
        authService.handleSessionExpired();
        throw new Error('Sessão expirada');
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      showSuccess('Contexto atualizado!');
      if (onSave) onSave(null);
    } catch (err) {
      console.error('Erro ao salvar contexto da NC:', err);
      // 401 já mostrou o aviso de sessão expirada acima — não duplica toast.
      if (err.message !== 'Sessão expirada') {
        showError(`Erro ao salvar contexto: ${err.message}`);
      }
      throw err;
    }
  };

  // #77 Felipe (Sessão 27) — handler do PATCH /observacoes.
  // Endpoint dedicado: não passa pelo /editar-analise, não dispara
  // reanálise IA, não loga via log_human_edit (campo livre, não-derivado).
  const handleSaveObservacoes = async () => {
    if (savingObservacoes) return;
    setSavingObservacoes(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/observacoes`, {
        method: 'PATCH',
        body: JSON.stringify({ observacoes }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      // Atualizar baseline pro botão "Salvar" sumir e dirty flag resetar
      setObservacoesOriginal(observacoes);
      showSuccess('Observações salvas.');
      if (onSave) onSave(null);
    } catch (err) {
      console.error('Erro ao salvar observações:', err);
      showError(`Erro ao salvar observações: ${err.message}`);
    } finally {
      setSavingObservacoes(false);
    }
  };

  // Helper: True se observações tem mudança não salva
  const observacoesDirty = observacoes.trim() !== (observacoesOriginal || '').trim();

  // Sessão 19 — Ações Preventivas: CRUD via dot notation.
  // Backend já aceita acoes_preventivas no whitelist
  // `_ARRAYS_APPEND_IMPLICITO` (analysis_field_validator.py).
  // - Para EDITAR um campo de ação existente: campo
  //   `acoes_preventivas.{idx}.{field}` (descricao, abrangencia,
  //   responsavel_funcao, prazo).
  // - Para ADICIONAR uma nova ação: campo `acoes_preventivas.+` com
  //   valor objeto (backend resolve o índice).
  // - Para REMOVER: campo `acoes_preventivas.-{idx}` (suporte canônico
  //   do TD-144).
  const handleSavePreventivaField = async (idx, field, valor) => {
    return handleSaveFieldV4(`acoes_preventivas.${idx}.${field}`, valor);
  };

  const handleAddPreventiva = async () => {
    try {
      const result = await editarAnaliseFn(ncId, [{
        campo: 'acoes_preventivas.+',
        valor: {
          descricao: '',
          abrangencia: '',
          responsavel_funcao: '',
          prazo: '',
        },
      }], { override: overrideMode });
      if (result.status === 'success' || result.status === 'preview') {
        showSuccess('Ação preventiva adicionada!');
        if (onSave) onSave(null);
      }
    } catch (err) {
      console.error('Erro ao adicionar ação preventiva:', err);
      showError(`Erro ao adicionar: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const handleRemovePreventiva = async (idx) => {
    if (!window.confirm(`Remover Ação Preventiva ${idx + 1}?`)) return;
    try {
      const result = await editarAnaliseFn(ncId, [{
        campo: `acoes_preventivas.-${idx}`,
        valor: null,
      }], { override: overrideMode });
      if (result.status === 'success' || result.status === 'preview') {
        showSuccess('Ação preventiva removida!');
        if (onSave) onSave(null);
      }
    } catch (err) {
      console.error('Erro ao remover ação preventiva:', err);
      showError(`Erro ao remover: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const handleReanalisarParcial6M = async () => {
    setLoading(true);
    setLoadingMessage('A IA está reanalisando os porquês a partir do 6M — pode levar até ~30s...');
    try {
      // Sprint-2 fix (Sintoma 3 — "Ishikawa some ao editar 1 campo"):
      // 1. Filtra `pilar_principal` (metadado, não dimensão Ishikawa)
      // 2. Filtra entries com valor null/undefined/'' — evita gravar
      //    `null` em pilares que estavam preenchidos.
      // 3. Logs detalhados para coleta de evidência (manter 2-4 semanas).
      const analise6m = analiseV4?.compreensao?.analise_6m || {};
      const PILARES_VALIDOS = ['mao_de_obra', 'metodo', 'maquina', 'material', 'medicao', 'meio_ambiente'];

      const edicoes6m = [];
      const ignored = [];
      for (const [key, val] of Object.entries(analise6m)) {
        if (key === 'pilar_principal') {
          ignored.push({ key, motivo: 'metadado' });
          continue;
        }
        if (!PILARES_VALIDOS.includes(key)) {
          ignored.push({ key, motivo: 'fora dos 6 pilares canônicos' });
          continue;
        }
        if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
          ignored.push({ key, motivo: 'valor vazio/nulo' });
          continue;
        }
        edicoes6m.push({
          campo: `compreensao.analise_6m.${key}`,
          valor: val,
        });
      }

      console.log('[Ishikawa-Sprint2] Reanálise parcial 6M', {
        analise6m_keys: Object.keys(analise6m),
        edicoes_enviadas: edicoes6m.length,
        edicoes: edicoes6m.map(e => ({ campo: e.campo, len: typeof e.valor === 'string' ? e.valor.length : null })),
        ignored,
      });

      if (edicoes6m.length === 0) {
        console.warn('[Ishikawa-Sprint2] Nenhum pilar válido para enviar — abortando.');
        showWarning('Nenhum pilar do Ishikawa preenchido para reanalisar.');
        setLoading(false);
        return;
      }

      // #189 P2 (fast-follow 6M) — regenAcoes: handshake do regen seletivo (o
      // backend só inclui ações no preview se a flag env do ambiente estiver ON;
      // sem ela o response vem sem os campos de ações e a UI degrada pro atual).
      const result = await editarAnaliseFn(ncId, edicoes6m, { reanalisarParcial: true, regenAcoes: true });

      if (result.status === 'preview' && result.campos_reanalisados) {
        // Sessão 17 — aviso de revisão de ações corretivas. A reanálise
        // regenera causa raiz + Porquês mas NÃO as ações corretivas; se a
        // causa raiz mudou, as ações podem ter ficado desalinhadas dela
        // (e o score de qualidade cai). Backend sinaliza via
        // acoes_revisao_sugerida / acoes_revisao_mensagem no response.
        const msgRevisaoAcoes = result.acoes_revisao_mensagem ||
          'A causa raiz mudou — revise as ações corretivas para mantê-las alinhadas.';
        // #204: prévia em modal (antes: window.confirm denso). O aceite roda
        // em handleAceitarPreview — fluxo preview→confirmar do backend idêntico.
        setPreviewReanalise({
          tipo: '6m',
          edicoes: edicoes6m,
          camposReanalisados: result.campos_reanalisados,
          causaAnterior: analiseV4?.causa_raiz?.declaracao ||
            (typeof analise?.causa_raiz === 'string' ? analise.causa_raiz : '') || '',
          novaCausa: result.campos_reanalisados?.causa_raiz?.declaracao || 'N/A',
          porquesGerados: (result.campos_reanalisados?.porques || []).length,
          avisoAcoes: result.acoes_revisao_sugerida ? msgRevisaoAcoes : null,
          // #189 P2 (fast-follow) — dados do regen seletivo pro modal (ausentes = flag off)
          acoesMergePreview: result.campos_reanalisados?.acoes_merge_preview || null,
          acoesPreservadasIds: result.campos_reanalisados?.acoes_preservadas_ids || [],
          acoesRegeneradasCount: result.campos_reanalisados?.acoes_regeneradas_count || 0,
        });
      } else {
        showSuccess('Campos atualizados!');
        // TD143-followup-useeffect: caminho sem preview (campos 6M aceitos
        // direto, sem reanálise IA) — limpa o dirty de porquês+causa pro
        // re-sync aplicar a prop nova ao state local.
        setPorquesDirty(false);
        setCausaDirty(false);
        if (onSave) onSave(null);
      }
    } catch (err) {
      console.error('Erro na reanálise parcial:', err);
      // #271 — 401 = sessão expirada: para o spinner (finally), avisa de forma
      // humana e aciona a mecânica de encerramento (#145/#215) em vez do erro
      // genérico de IA, que aqui seria enganoso.
      if (err.response?.status === 401) {
        showInfo('Sua sessão expirou — faça login novamente para continuar.');
        authService.handleSessionExpired();
      } else {
        showError('Erro na reanálise parcial. Edições manuais foram preservadas.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('Processando...');
    }
  };

  // Fase 2A (#188) — Recalcular causa raiz a partir dos PORQUÊS editados.
  // Espelha handleReanalisarParcial6M, mas o ponto de partida da IA são os
  // porquês (não o 6M): só faz sentido no Registro, que NÃO tem 6M. Reusa
  // buildPorquesEdicoes (#91) pra coletar APENAS os porquês alterados —
  // mesmo diff usado no handleSave (anti-fantasma + porquês 6-10). O ciclo
  // preview → window.confirm → confirmar é idêntico ao 6M.
  const handleReanalisarPorques = async () => {
    setLoading(true);
    setLoadingMessage('A IA está recalculando a causa raiz a partir dos porquês — pode levar até ~30s...');
    try {
      let edicoesPorques = buildPorquesEdicoes(
        editedData.cinco_porques,
        analise?.cinco_porques
      );

      // #219 Fase 2 — fix do beco "salvei os porquês e o Recalcular recusa":
      // sem edição local pendente, re-emite a cadeia SALVA inteira (o backend
      // exige edições de porquês na request pra rodar a derivação). No-op de
      // escrita; o confirm já marcava os porquês como editados-humano antes.
      if (edicoesPorques.length === 0) {
        edicoesPorques = buildPorquesEdicoesCadeiaCompleta(analise?.cinco_porques);
      }

      if (edicoesPorques.length === 0) {
        showWarning('A cadeia de porquês está vazia — preencha ao menos um porquê antes de recalcular.');
        setLoading(false);
        return;
      }

      // #189 P2 — regenAcoes: handshake do regen seletivo (o backend só inclui
      // ações no preview se a flag env do ambiente estiver ON; sem ela, o
      // response vem sem os campos de ações e a UI degrada pro fluxo atual).
      const result = await editarAnaliseFn(ncId, edicoesPorques, { reanalisarPorques: true, regenAcoes: true });

      if (result.status === 'preview' && result.campos_reanalisados) {
        // Mesmo aviso do 6M (Sessão 17): a reanálise regenera a causa raiz mas
        // NÃO as ações corretivas; se a causa mudou, as ações podem ter ficado
        // desalinhadas. Backend sinaliza via acoes_revisao_sugerida/_mensagem.
        // (#189 P2: com regen no preview o backend SUPRIME o aviso — as ações
        // serão realinhadas no aceite.)
        const msgRevisaoAcoes = result.acoes_revisao_mensagem ||
          'A causa raiz mudou — revise as ações corretivas para mantê-las alinhadas.';
        // #204: prévia em modal (antes: window.confirm denso).
        setPreviewReanalise({
          tipo: 'porques',
          edicoes: edicoesPorques,
          camposReanalisados: result.campos_reanalisados,
          causaAnterior: analiseV4?.causa_raiz?.declaracao ||
            (typeof analise?.causa_raiz === 'string' ? analise.causa_raiz : '') || '',
          novaCausa: result.campos_reanalisados?.causa_raiz?.declaracao || 'N/A',
          avisoAcoes: result.acoes_revisao_sugerida ? msgRevisaoAcoes : null,
          // #189 P2 — dados do regen seletivo pro modal (ausentes = flag off)
          acoesMergePreview: result.campos_reanalisados?.acoes_merge_preview || null,
          acoesPreservadasIds: result.campos_reanalisados?.acoes_preservadas_ids || [],
          acoesRegeneradasCount: result.campos_reanalisados?.acoes_regeneradas_count || 0,
        });
      } else {
        showSuccess('Porquês atualizados!');
        // Caminho sem preview (porquês aceitos direto, sem reanálise IA) —
        // limpa o dirty + fecha a edição pro re-sync aplicar a prop nova.
        setPorquesDirty(false);
        setCausaDirty(false);
        setPorquesEdit(false);
        if (onSave) onSave(null);
      }
    } catch (err) {
      console.error('Erro ao recalcular causa raiz a partir dos porquês:', err);
      // #271 — 401 = sessão expirada: para o spinner (finally), avisa de forma
      // humana e aciona a mecânica de encerramento (#145/#215).
      if (err.response?.status === 401) {
        showInfo('Sua sessão expirou — faça login novamente para continuar.');
        authService.handleSessionExpired();
      } else {
        showError('Erro ao recalcular. Edições manuais foram preservadas.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('Processando...');
    }
  };

  // #204: aceite da prévia — 2ª chamada com confirmarReanalise (mesmo fluxo
  // backend do antigo confirm). Pós-aceite preserva a lógica original:
  // toast + aviso de ações + limpa hasUnsavedChanges (TD143-followup-useeffect:
  // os campos foram persistidos; a flag limpa deixa o useEffect re-sincronizar
  // a prop nova ao state local — senão "Salvar" depois reverteria) + refresh.
  const handleAceitarPreview = async () => {
    if (!previewReanalise || aceitandoPreview) return;
    const p = previewReanalise;
    setAceitandoPreview(true);
    try {
      // #189 P2 — regenAcoes no confirm também (o backend RE-MESCLA contra o
      // estado atual; preview_aceito carrega as acoes_ia cruas validadas). Vale
      // pros DOIS ramos: 6M (reanalisarParcial) e porquês (reanalisarPorques).
      const flags = p.tipo === '6m'
        ? { reanalisarParcial: true, confirmarReanalise: true, previewAceito: p.camposReanalisados, regenAcoes: true }
        : { reanalisarPorques: true, confirmarReanalise: true, previewAceito: p.camposReanalisados, regenAcoes: true };
      await editarAnaliseFn(ncId, p.edicoes, flags);
      const _baseMsg = p.tipo === '6m' ? 'Reanálise parcial aplicada!' : 'Causa raiz recalculada a partir dos porquês!';
      showSuccess(p.acoesRegeneradasCount > 0
        ? `${_baseMsg} ${p.acoesRegeneradasCount} ação(ões) atualizada(s) pra nova causa.`
        : _baseMsg);
      if (p.avisoAcoes) showWarning(p.avisoAcoes);
      // Cadeia + causa persistidas pela IA → limpa dirty e fecha as edições
      // dessas seções pro re-sync TD-146 aplicar a prop nova ao state local.
      setPorquesDirty(false);
      setCausaDirty(false);
      setPorquesEdit(false);
      setCausaEdit(false);
      setPreviewReanalise(null);
      if (onSave) onSave(null);
    } catch (err) {
      console.error('Erro ao aplicar reanálise:', err);
      showError('Erro ao aplicar a reanálise. Edições manuais foram preservadas.');
    } finally {
      setAceitandoPreview(false);
    }
  };

  const handleCancelarPreview = () => {
    if (aceitandoPreview) return;
    showWarning(previewReanalise?.tipo === '6m'
      ? 'Reanálise descartada. Edições do 6M foram mantidas.'
      : 'Recálculo descartado. Edições dos porquês foram mantidas.');
    setPreviewReanalise(null);
  };

  // Pilares 6M válidos
  const PILARES_6M = ['Mão de obra', 'Método', 'Máquina', 'Material', 'Medição', 'Meio ambiente'];
  const PILAR_DISPLAY_NAMES = {
    mao_de_obra: 'Mão de Obra', metodo: 'Método', maquina: 'Máquina',
    material: 'Material', medicao: 'Medição', meio_ambiente: 'Meio Ambiente',
  };

  // #235 — perguntas-guia por pilar (condensadas do guia de resolução de
  // problemas usado por montadoras/IATF com fornecedores). Aparecem como
  // placeholder do pilar vazio + no bloco "Perguntas que ajudam" da seção 6M.
  const PILAR_HINTS_6M = {
    mao_de_obra: 'O operador conhece e segue o padrão de trabalho? É qualificado e tem experiência pra tarefa? Conhece os critérios de aceitação?',
    metodo: 'O padrão de trabalho é adequado e está disponível no posto? A sequência de operações e o setup estão corretos? O método garante a qualidade?',
    maquina: 'O equipamento atende às especificações? A manutenção preventiva (TPM) está em dia? Houve falha recente, ruído incomum ou condição técnica ruim?',
    material: 'O material está conforme a especificação (aparência, contaminação)? Houve problema com semiacabados, manuseio ou transporte entre operações?',
    medicao: 'O instrumento é adequado, aprovado e calibrado? A leitura do parâmetro está correta? A frequência de medição é suficiente?',
    meio_ambiente: '5S, temperatura, umidade, iluminação e ventilação estão adequados? O ambiente é seguro e ergonômico?',
  };

  // Nível 1 follow-up (feedback Francis, Sessão 44) — pílula de staleness no
  // CABEÇALHO da seção: as seções colapsam por padrão e o chip explicativo fica
  // DENTRO → sem isto o aviso ficava invisível até expandir. A pílula é o
  // marcador sempre-visível; o chip interno explica o porquê. Determinístico.
  const stalePill = (
    <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '999px', padding: '2px 8px', verticalAlign: 'middle' }}>
      revisar
    </span>
  );

  return (
    <div className="nc-editor">
      {loading && <LoadingSpinner overlay message={loadingMessage} />}

      {/* #204: prévia das reanálises parciais em modal (ex-window.confirm) */}
      <PreviewReanaliseModal
        preview={previewReanalise}
        aceitando={aceitandoPreview}
        onAceitar={handleAceitarPreview}
        onCancelar={handleCancelarPreview}
      />
      
      {/* #219 Fase 2 — banner de rascunho recuperado (#145), agora no topo do
          editor sempre-visível (o modo "recolhido" com botão mestre acabou). */}
      {rascunhoRecuperavel && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', marginBottom: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', borderRadius: '6px' }}>
          <span className="material-symbols-outlined" style={{ color: '#b45309', fontSize: '20px' }}>history</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '2px' }}>Edições não salvas recuperadas</div>
            <div style={{ fontSize: '13px', color: '#78350f' }}>
              Sua sessão expirou enquanto você editava esta NC. Salvamos seu rascunho — restaure para continuar de onde parou.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <Button icon="restore" onClick={handleRestaurarRascunho}>Restaurar edições</Button>
              <Button variant="secondary" onClick={handleDescartarRascunho}>Descartar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Guia de sequência (item 2 #191) — Registro: orienta porquês→causa→ações.
          Advisory, não força ordem. A causa deriva do ÚLTIMO porquê. */}
      {isRegistro && (
        <div className="editor-guia-sequencia" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #006948', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', fontWeight: 700, color: '#006948' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>route</span>
            <span>1. Porquês</span>
            <span style={{ opacity: 0.6 }}>→</span>
            <span>2. Causa raiz</span>
            <span style={{ opacity: 0.6 }}>→</span>
            <span>3. Ações</span>
          </div>
          <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px', lineHeight: 1.5 }}>
            A causa raiz vem do <strong>último porquê</strong> — complete a cadeia até a raiz mais profunda antes de recalcular. Edite os porquês e use <strong>"Recalcular causa raiz a partir dos porquês"</strong>.
            {' '}<em>Dica: valide a lógica lendo de baixo pra cima com <strong>"portanto"</strong> — se cada "portanto" fizer sentido, a cadeia está sólida.</em>
          </div>
        </div>
      )}

      {/* Guia da Investigação (#193 I-A) — spine 6M→porquês→causa→ações.
          Espelha o guia do Registro, pro fluxo 6M-first. Gated {!isRegistro}.
          Posicionado ANTES do override (orientação antes do atalho de power-user). */}
      {!isRegistro && (
        <div className="editor-guia-sequencia" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #006948', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', fontWeight: 700, color: '#006948' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>route</span>
            <span>1. 6M</span>
            <span style={{ opacity: 0.6 }}>→</span>
            <span>2. Porquês</span>
            <span style={{ opacity: 0.6 }}>→</span>
            <span>3. Causa raiz</span>
            <span style={{ opacity: 0.6 }}>→</span>
            <span>4. Ações</span>
          </div>
          <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px', lineHeight: 1.5 }}>
            O <strong>pilar do 6M</strong> dirige os porquês; a <strong>causa vem do último porquê</strong>.{' '}
            {allowIaRecalc
              ? <>Edite o 6M e use <strong>"Reanalisar Porquês com base no 6M"</strong>; depois refine os porquês e recalcule a causa.</>
              : <>Edite cada campo no <strong>lápis ✏️</strong> seguindo essa ordem — suas mudanças são salvas direto.</>}
            {' '}<em>Dica: valide a lógica lendo de baixo pra cima com <strong>"portanto"</strong> — se cada "portanto" fizer sentido, a cadeia está sólida.</em>
          </div>
        </div>
      )}

      {/* #219 follow-up — Orientação de edição (substitui o toggle "Editar
          livremente (sem IA)", que era redundante: o save JÁ é sempre limpo).
          Explicita ao leigo a regra que ele não enxerga: lápis = grava direto,
          sem IA; a IA só roda nos botões de recalcular. */}
      <div className="editor-orientacao-edicao" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#64748b', flexShrink: 0, marginTop: '1px' }}>info</span>
        <span>
          Clique no <strong>lápis ✏️</strong> de qualquer campo para editar — <strong>grava direto{allowIaRecalc ? ', sem IA' : ''}</strong>.
          {allowIaRecalc && <>
            {' '}A IA só refaz a análise quando você usa <strong>"Recalcular causa raiz a partir dos porquês"</strong>
            {!isRegistro && <> ou <strong>"Reanalisar Porquês com base no 6M"</strong></>}.
          </>}
        </span>
      </div>

      {/* #71/#73 (épico fluxo 8D, decisão Francis 07/07: sem esperar Felipe) —
          Ação Imediata/Contenção ANTES da investigação: no método (D3 do 8D),
          conter vem antes de analisar a causa. Era a antepenúltima seção. */}
      <div className="editor-section">
        <div className="section-header" onClick={() => toggleSection('contencao')}>
          <h4>
            <span className="material-symbols-outlined section-chevron">{expandedSections['contencao'] ? 'expand_more' : 'chevron_right'}</span>
            Ação Imediata / Contenção
          </h4>
        </div>
        {expandedSections['contencao'] && (
          <div className="section-content">
            <AnalysisFieldEditor label="Descrição" campo="acao_contencao_imediata.descricao" valorAtual={analiseV4?.acao_contencao_imediata?.descricao} tipo="textarea" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('acao_contencao_imediata.descricao')} disabled={false} ajuda="Medida de EMERGÊNCIA para estancar o problema agora, antes de resolver a causa. Ex: bloquear e segregar o lote suspeito. (Diferente da ação corretiva, que elimina a causa.)" />
            <AnalysisFieldEditor label="Prazo (horas)" campo="acao_contencao_imediata.prazo_horas" valorAtual={analiseV4?.acao_contencao_imediata?.prazo_horas} tipo="number" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('acao_contencao_imediata.prazo_horas')} disabled={false} />
            <AnalysisFieldEditor label="Responsável" campo="acao_contencao_imediata.responsavel_funcao" valorAtual={analiseV4?.acao_contencao_imediata?.responsavel_funcao} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('acao_contencao_imediata.responsavel_funcao')} disabled={false} />
            <AnalysisFieldEditor label="Critério de Conclusão" campo="acao_contencao_imediata.criterio_conclusao" valorAtual={analiseV4?.acao_contencao_imediata?.criterio_conclusao} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('acao_contencao_imediata.criterio_conclusao')} disabled={false} />
            {/* #70 (fluxo 8D) — frentes estruturadas: Estoque/Trânsito/Cliente/
                Linha com qtd total × NOK (% computado ao vivo). */}
            <FrentesContencaoEditor
              frentes={analiseV4?.acao_contencao_imediata?.frentes}
              onSave={(lista) => handleSaveFieldV4('acao_contencao_imediata.frentes', lista)}
              disabled={false}
            />
          </div>
        )}
      </div>

      {/* #69 (fluxo 8D) — Abrangência multi-dimensional (D2): entre a
          contenção (D3... na ordem de TELA vem antes por ser ação imediata)
          e a investigação. Estende o MVP #280 (checkbox geral na Disposição). */}
      <div className="editor-section">
        <div className="section-header" onClick={() => toggleSection('abrangencia')}>
          <h4>
            <span className="material-symbols-outlined section-chevron">{expandedSections['abrangencia'] ? 'expand_more' : 'chevron_right'}</span>
            Abrangência do problema
          </h4>
        </div>
        {expandedSections['abrangencia'] && (
          <div className="section-content">
            <AbrangenciaEditor
              abrangencia={analiseV4?.abrangencia}
              onSave={(lista) => handleSaveFieldV4('abrangencia', lista)}
              disabled={false}
            />
          </div>
        )}
      </div>

      {/* #242 (épico automotivo) — TRIAGEM 6M (verde/vermelho): overlay manual
          de priorização dos pilares como suspeita × a-testar. Adjacente ao 6M. */}
      <div className="editor-section">
        <div className="section-header" onClick={() => toggleSection('triagem_6m')}>
          <h4>
            <span className="material-symbols-outlined section-chevron">{expandedSections['triagem_6m'] ? 'expand_more' : 'chevron_right'}</span>
            Triagem 6M (priorização)
          </h4>
        </div>
        {expandedSections['triagem_6m'] && (
          <div className="section-content">
            <Triagem6MEditor
              triagem={analiseV4?.triagem_6m}
              onSave={(lista) => handleSaveFieldV4('triagem_6m', lista)}
              disabled={false}
            />
          </div>
        )}
      </div>

      {/* Análise 6M (Ishikawa) — movida pro TOPO da spine (Investigação #193 I-A),
          expandida por padrão, com o botão "Reanalisar Porquês com base no 6M"
          junto. #71: a contenção agora vem ANTES dela (fluxo 8D). */}
      {!isRegistro && analiseV4 && (
        <div className="editor-section">
          <div className="section-header" id="editor-secao-6m" onClick={() => toggleSection('6m')}>
            <h4>
              <span className="material-symbols-outlined section-chevron">{expandedSections['6m'] ? 'expand_more' : 'chevron_right'}</span>
              Análise 6M (Ishikawa)
            </h4>
          </div>
          {expandedSections['6m'] && (
            <div className="section-content">
              {/* #235 — perguntas-guia por pilar (colapsado por padrão pra não
                  poluir; método das montadoras/IATF). Inclui a regra de ouro do
                  pilar Mão de Obra (só o operador direto da etapa). */}
              <details style={{ marginBottom: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>help</span>
                  Perguntas que ajudam a preencher cada pilar
                </summary>
                <ul style={{ margin: '8px 0 4px', paddingLeft: '18px', fontSize: '12px', color: '#475569', lineHeight: 1.6 }}>
                  {['mao_de_obra', 'metodo', 'maquina', 'material', 'medicao', 'meio_ambiente'].map(pilar => (
                    <li key={pilar}><strong>{PILAR_DISPLAY_NAMES[pilar]}:</strong> {PILAR_HINTS_6M[pilar]}</li>
                  ))}
                </ul>
                <p style={{ margin: '6px 0 2px', fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
                  <strong>Regra de ouro — Mão de Obra:</strong> esse pilar é só do <strong>operador direto</strong> da etapa.
                  Erro de quem não opera a etapa (ex.: técnico de manutenção) entra em <strong>Máquina</strong> ou <strong>Método</strong>,
                  não em Mão de Obra — evita atribuir tudo "ao operador".
                </p>
              </details>
              {['mao_de_obra', 'metodo', 'maquina', 'material', 'medicao', 'meio_ambiente'].map(pilar => (
                <AnalysisFieldEditor
                  key={pilar}
                  label={PILAR_DISPLAY_NAMES[pilar] || pilar.replace(/_/g, ' ')}
                  campo={`compreensao.analise_6m.${pilar}`}
                  valorAtual={analiseV4?.compreensao?.analise_6m?.[pilar]}
                  tipo="textarea"
                  rows={2}
                  onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange}
                  editadoPorHumano={isEditadoPorHumano(`compreensao.analise_6m.${pilar}`)}
                  disabled={false}
                  placeholder={PILAR_HINTS_6M[pilar] || `Análise do pilar ${pilar}...`}
                  ajuda={PILAR_HINTS_6M[pilar] || ''}
                />
              ))}
              <AnalysisFieldEditor
                label="Pilar Principal"
                campo="compreensao.analise_6m.pilar_principal"
                valorAtual={analiseV4?.compreensao?.analise_6m?.pilar_principal}
                tipo="select"
                opcoes={PILARES_6M}
                onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange}
                editadoPorHumano={isEditadoPorHumano('compreensao.analise_6m.pilar_principal')}
                disabled={false}
              />
              {/* Aviso de pilar incoerente (Sessão 21) — heurística espelhada em
                  pilarCoerencia.js; não bloqueia, só sinaliza antes de reanalisar. */}
              {(() => {
                const _diag = diagnosticarPilarPrincipal(
                  analiseV4?.compreensao?.analise_6m?.pilar_principal,
                  analiseV4?.compreensao?.analise_6m
                );
                if (_diag.coerente) return null;
                return (
                  <div className="aviso-pilar-incoerente" role="alert">
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>warning</span>
                    <strong>Pilar potencialmente incoerente:</strong> {_diag.motivo}
                  </div>
                );
              })()}
              {/* I-B (#193) — chip de alinhamento 6M→porquês (DETERMINÍSTICO).
                  6M editado mas porquês NÃO re-derivados → os porquês ficaram
                  velhos vs o 6M; convida a reanalisar. Lê a flag persistida. */}
              {analiseV4?.metadados_ia?.porques_desalinhados_do_6m && (
                <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '12px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>sync</span>
                  <span>Os porquês <strong>não refletem mais o 6M</strong> editado — {allowIaRecalc ? 'reanalise os porquês a partir do 6M.' : 'edite os porquês manualmente para realinhar.'}</span>
                </div>
              )}
              {allowIaRecalc && (
                <button
                  className="btn-reanalisar-parcial"
                  onClick={handleReanalisarParcial6M}
                  disabled={loading}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>autorenew</span>
                  {loading ? 'Reanalisando...' : 'Reanalisar Porquês com base no 6M editado'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* 5 Porquês (reordenado — porquês ACIMA da causa: são o input; a causa
          deriva deles). #219 Fase 2: lápis NA SEÇÃO — a cadeia é uma unidade
          lógica (Validar/Recalcular operam nela inteira), então edita-se a
          cadeia e salva-se a cadeia (diff anti-fantasma de sempre). */}
      <div className="editor-field">
        <div className="secao-legada-header">
          {/* #219 fix: heading (não label) — semântica de seção (a11y) +
              os testes E2E JSON-first procuram getByRole('heading'). */}
          <h4 className="secao-legada-titulo" id="editor-secao-porques">Porquês</h4>
          {porquesEdit ? (
            <div className="secao-legada-acoes">
              {porquesDirty && <span className="secao-dirty">● Alterações não salvas</span>}
              <Button size="sm" variant="secondary" onClick={cancelarEdicaoPorques} disabled={loading}>Cancelar</Button>
              <Button size="sm" icon="save" onClick={handleSalvarPorques} disabled={loading}>Salvar porquês</Button>
            </div>
          ) : (
            <button
              type="button"
              className="afe-btn-edit"
              onClick={abrirEdicaoPorques}
              title="Editar porquês"
              aria-label="Editar porquês"
            >
              <span className="material-symbols-outlined afe-edit-icon">edit</span>
            </button>
          )}
        </div>
        {!porquesEdit && (
          editedData.cinco_porques.some(p => (p || '').trim()) ? (
            <ol className="porques-display">
              {editedData.cinco_porques
                .filter(p => (p || '').trim())
                .map((p, i) => <li key={i}>{p}</li>)}
            </ol>
          ) : (
            <p className="porques-display-vazio">Nenhum porquê registrado ainda — use o lápis ou "Adicionar porquê".</p>
          )
        )}
        {porquesEdit && editedData.cinco_porques.map((porque, index) => (
          <div key={index} className="porque-item-enhanced">
            <div className="porque-header">
              <span className="porque-label">Por quê {index + 1}?</span>
              {porqueEditado[index] && (
                <span className="badge-editado">
                  <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>edit</span>
                  Editado
                </span>
              )}
            </div>
            <div className="porque-input-group">
              <textarea
                value={porque}
                onChange={(e) => handlePorqueChange(index, e.target.value)}
                placeholder={`Resposta ${index + 1}...`}
                rows="2"
              />
            </div>
            {index === 0 && (
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Por que isso aconteceu? Responda com base na linha de cima. Ex: "Saiu fora de medida" → "molde desgastado" → "passou do prazo de troca"…
              </small>
            )}
            {validacaoConsistencia && validacaoConsistencia.alertas && 
             validacaoConsistencia.alertas.find(a => a.indice === index) && (
              <div className={`alerta-porque alerta-${validacaoConsistencia.alertas.find(a => a.indice === index).tipo}`}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>warning</span>
                {validacaoConsistencia.alertas.find(a => a.indice === index).mensagem}
              </div>
            )}
          </div>
        ))}
        
        {/* Item 4 (#191) — Adicionar porquê (par do guia): cresce a cadeia.
            Backend já aceita o append (provado). Cap visual em 10 = teto de geração. */}
        {editedData.cinco_porques.length < 10 && (
          <button
            type="button"
            className="btn-adicionar-porque"
            onClick={handleAdicionarPorque}
            disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '4px', marginBottom: '4px', padding: '8px 14px', background: '#ffffff', border: '1px dashed #006948', color: '#006948', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>add</span>
            Adicionar porquê
          </button>
        )}

        {/* Validação de Consistência */}
        {validacaoConsistencia && (
          <div className={`validacao-consistencia ${validacaoConsistencia.valido ? 'valida' : 'invalida'}`}>
            <div className="validacao-header">
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                {validacaoConsistencia.valido ? 'check_circle' : 'warning'}
              </span>
              Cadeia (lógica interna): {validacaoConsistencia.score}/100
            </div>
            {validacaoConsistencia.alertas && validacaoConsistencia.alertas.length > 0 && (
              <ul className="validacao-alertas">
                {validacaoConsistencia.alertas.filter(a => a.indice === null || a.indice === undefined).map((alerta, idx) => (
                  <li key={idx} className={`alerta-${alerta.tipo}`}>
                    {alerta.mensagem}
                  </li>
                ))}
              </ul>
            )}
            {validacaoConsistencia.sugestoes && validacaoConsistencia.sugestoes.length > 0 && (
              <div className="validacao-sugestoes">
                <strong>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '4px' }}>lightbulb</span>
                  Sugestões:
                </strong>
                <ul>
                  {validacaoConsistencia.sugestoes.map((sug, idx) => (
                    <li key={idx}>{sug}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {allowIaRecalc && (
          <button
            className="btn-validar"
            onClick={handleValidarConsistencia}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" />
                Validando...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined btn-ai-icon">fact_check</span>
                Validar Consistência
              </>
            )}
          </button>
        )}

        {/* Item B3 (#192) — chip de ALINHAMENTO causa↔porquês: DETERMINÍSTICO
            (código, não IA — é fato, não julgamento). Aparece automático (sem
            clicar Validar) quando os porquês foram editados mas a causa ainda
            não foi recalculada → a causa não reflete mais a cadeia. Some após o
            recálculo. É um CONVITE a recalcular, não um score de "incoerente". */}
        {/* I-B (#193): chip ungateado pros 2 modos (era isRegistro-only) — o link
            porquês→causa vale também na Investigação. */}
        {(analiseV4?.metadados_ia?.causa_desalinhada_dos_porques || (hasUnsavedChanges && porqueEditado.some(Boolean))) && (
          <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '12px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>sync</span>
            <span>A causa raiz <strong>não reflete mais a cadeia</strong> de porquês — {allowIaRecalc ? 'recalcule para realinhar.' : 'edite a causa manualmente para realinhar.'}</span>
          </div>
        )}

        {/* Fase 2A (#188) + I-B (#193) — Recalcular causa raiz a partir dos
            porquês. Disponível nos 2 modos: no Registro é o único recalc; na
            Investigação coexiste com "Reanalisar Porquês com base no 6M" — este
            é o link porquês→causa (refinar um porquê e recalcular só a causa,
            sem re-rodar o 6M, que sobrescreveria os porquês). */}
        {allowIaRecalc && analiseV4 && (
          <button
            className="btn-reanalisar-parcial"
            onClick={handleReanalisarPorques}
            disabled={loading}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>autorenew</span>
            {loading ? 'Recalculando...' : 'Recalcular causa raiz a partir dos porquês'}
          </button>
        )}
      </div>

      {/* #241 (épico automotivo) — CADEIA DE DETECÇÃO (D4 do 8D): "por que
          NÃO foi detectado?". Adjacente aos porquês (que são a vertente
          ocorrência). Recolhível — não pesa pra quem não usa 8D. */}
      <div className="editor-section">
        <div className="section-header" onClick={() => toggleSection('porques_deteccao')}>
          <h4>
            <span className="material-symbols-outlined section-chevron">{expandedSections['porques_deteccao'] ? 'expand_more' : 'chevron_right'}</span>
            Por que não foi detectado? (cadeia de detecção)
          </h4>
        </div>
        {expandedSections['porques_deteccao'] && (
          <div className="section-content">
            <PorquesDeteccaoEditor
              porques={analiseV4?.porques_deteccao}
              onSave={(lista) => handleSaveFieldV4('porques_deteccao', lista)}
              disabled={false}
            />
          </div>
        )}
      </div>

      {/* Causa Raiz (reordenado — ABAIXO dos porquês: deriva do último porquê).
          #219 Fase 2: lápis na seção; leitura ganha confiança + justificativa
          (antes só existiam no bloco read-only do NCResultAnalysisSection). */}
      <div className="editor-field">
        <div className="secao-legada-header">
          <h4 className="secao-legada-titulo" id="editor-secao-causa">Causa Raiz</h4>
          {isEditadoPorHumano('causa_raiz.declaracao') ? (
            <span className="badge-humano" title="Editado por humano">
              <span className="material-symbols-outlined afe-badge-icon">edit</span>
              Editado
            </span>
          ) : (
            <span className="badge-ia" title="Gerado pela IA">
              <span className="material-symbols-outlined afe-badge-icon">auto_awesome</span>
              IA
            </span>
          )}
          {causaEdit ? (
            <div className="secao-legada-acoes">
              {causaDirty && <span className="secao-dirty">● Alterações não salvas</span>}
              <Button size="sm" variant="secondary" onClick={cancelarEdicaoCausa} disabled={loading}>Cancelar</Button>
              <Button size="sm" icon="save" onClick={handleSalvarCausa} disabled={loading}>Salvar causa</Button>
            </div>
          ) : (
            <button
              type="button"
              className="afe-btn-edit"
              onClick={abrirEdicaoCausa}
              title="Editar causa raiz"
              aria-label="Editar causa raiz"
            >
              <span className="material-symbols-outlined afe-edit-icon">edit</span>
            </button>
          )}
        </div>
        <small style={{ fontSize: '11px', color: '#57657a', marginTop: '0', marginBottom: '8px', display: 'block' }}>
          A causa mais profunda — a que, se resolvida, impede o problema de voltar. Normalmente é o último porquê. Não é o sintoma, é a origem.
        </small>
        {causaEdit ? (
          <div className="field-with-ai">
            <textarea
              value={editedData.causa_raiz}
              onChange={(e) => handleFieldChange('causa_raiz', e.target.value)}
              rows="4"
              placeholder="Descreva a causa raiz..."
            />
          </div>
        ) : (
          <>
            <p className="causa-display">{analise?.causa_raiz || 'Não identificada'}</p>
            {/* Leitura migrada do bloco read-only (#219 Fase 2): confiança +
                justificativa da escolha — sumiriam em rascunho sem isto. */}
            {analiseV4?.causa_raiz?.confianca && (
              <div className="v4-causa-meta">
                <span className={`v4-confianca v4-confianca-${(analiseV4.causa_raiz.confianca || '').toLowerCase()}`}>
                  Confiança: {analiseV4.causa_raiz.confianca}
                </span>
                {analiseV4.causa_raiz.justificativa_escolha && (
                  <p className="v4-justificativa">{analiseV4.causa_raiz.justificativa_escolha}</p>
                )}
              </div>
            )}
          </>
        )}
        {/* Item 5 (Filosofia C, #192) — aviso leve quando a causa foi editada à
            mão: pode não refletir a cadeia de porquês. Lê a marcação humana já
            existente (campos_editados_humano). Informativo, não bloqueia.
            #192 polish: SUPRIMIDO quando o chip B3 ("não reflete mais a cadeia",
            ~L1322) já está ativo — senão os dois avisos redundantes aparecem
            juntos (porquês editados + causa hand-edited). O B3 é o acionável
            (convida a recalcular); este fica só pro caso causa-editada-sem-
            porquês-tocados. Mesma condição do B3, negada. */}
        {isRegistro && isEditadoPorHumano('causa_raiz.declaracao')
          && !(analiseV4?.metadados_ia?.causa_desalinhada_dos_porques || (hasUnsavedChanges && porqueEditado.some(Boolean))) && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>info</span>
            <span>Causa raiz <strong>editada à mão</strong> — pode não refletir a cadeia de porquês atual. Para realinhar, edite os porquês e use "Recalcular causa raiz a partir dos porquês".</span>
          </div>
        )}
      </div>

      {/* (#219 Fase 2: o textarea "Ação Imediata" legado foi REMOVIDO — editava
          o MESMO campo do backend (acao_contencao_imediata.descricao) que a
          seção "Ação de Contenção Imediata" abaixo, e o save em lote o
          re-emitia INCONDICIONALMENTE = marcava a contenção como editada-humano
          em todo Salvar. Uma superfície só, marcação fiel.) */}

      {/* Ações Corretivas — #219 Fase 2 (Opção A): lápis na SEÇÃO abre os cards
          ricos; Salvar da seção reusa o builder em lote (incl. remoção de cauda
          do #143). Leitura = cards read-only abaixo. */}
      <div className="editor-field">
        <div className="secao-legada-header">
          <h4 className="secao-legada-titulo">Ações Corretivas</h4>
          {acoesEdit ? (
            <div className="secao-legada-acoes">
              {acoesDirty && <span className="secao-dirty">● Alterações não salvas</span>}
              <Button size="sm" variant="secondary" onClick={cancelarEdicaoAcoes} disabled={loading}>Cancelar</Button>
              <Button size="sm" icon="save" onClick={handleSalvarAcoes} disabled={loading}>Salvar ações</Button>
            </div>
          ) : (
            <button
              type="button"
              className="afe-btn-edit"
              onClick={abrirEdicaoAcoes}
              title="Editar ações corretivas"
              aria-label="Editar ações corretivas"
            >
              <span className="material-symbols-outlined afe-edit-icon">edit</span>
            </button>
          )}
        </div>
        {/* Item 3 (Fase B2, #192) — aviso PERMANENTE de dessincronia: a causa
            raiz mudou e as ações podem estar desalinhadas. Persistido em
            metadados_ia.acoes_dessincronizadas (sobrevive a recarregar a página).
            Desliga só no clique explícito "Revisei as ações". */}
        {analiseV4?.metadados_ia?.acoes_dessincronizadas && (
          <div style={{ marginBottom: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#92400e' }}>sync_problem</span>
            <span style={{ flex: 1, minWidth: '200px', fontSize: '13px', color: '#92400e' }}>
              A <strong>causa raiz mudou</strong> — as ações corretivas podem ter ficado desalinhadas. Revise-as e, quando estiverem alinhadas à nova causa, confirme.
            </span>
            <button
              type="button"
              onClick={handleMarcarAcoesRevisadas}
              disabled={loading}
              style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Revisei as ações
            </button>
          </div>
        )}
        {/* #219 Fase 2 — modo LEITURA das ações: cards read-only a partir da
            prop (fresca; o buffer só é semeado ao abrir o lápis). */}
        {!acoesEdit && (
          (analise?.acoes_corretivas || []).length === 0 ? (
            <p className="porques-display-vazio">Nenhuma ação corretiva ainda — use o lápis ou "Adicionar Ação".</p>
          ) : (
            (analise?.acoes_corretivas || []).map((acao, index) => {
              // #219 — leitura robusta a qualquer shape (registro v4 / 5W2H /
              // legacy): o adapter resolve descricao/responsavel/prazo. Antes
              // lia `acao.acao` cru → mostrava "—" quando o shape usava
              // what/descricao (ex.: fixture mock dos testes E2E).
              const a = normalizarAcaoCorretiva(acao);
              // #66 — `concluida_em` vive no shape V4 (prop analiseV4). O prop
              // `analise` (legado) NÃO carrega esse campo: o sync legado
              // reconstrói as ações a partir do V4 mas dropa o concluida_em.
              // Lê do V4 pelo MESMO índice (arrays alinhados 1:1); fallback pro
              // legado por segurança. Sem isso o badge nunca acende (grava no V4,
              // lia do legado) — só era cosmético, o cron já pulava a ação.
              const concluidaEm = analiseV4?.acoes_corretivas?.[index]?.concluida_em || acao.concluida_em || null;
              // #241 — `vertente` também vive SÓ no shape V4 (mesma lição do
              // concluida_em acima): lê do analiseV4 pelo índice; grava direto
              // via handleSaveFieldV4 (save limpo de 1 campo, enum no backend).
              const vertenteAtual = analiseV4?.acoes_corretivas?.[index]?.vertente || '';
              return (
              <div key={index} className="acao-item acao-item-display">
                <div className="acao-header">
                  <span>
                    Ação {index + 1}
                    {!acaoCorretivaEditada(acao, index) && (
                      <span
                        className="acao-badge-ia"
                        title="Ainda é a sugestão original da IA — não foi revisada/editada por uma pessoa. Revise após uma reanálise."
                      >
                        não editada
                      </span>
                    )}
                  </span>
                  {/* #66 N2.3 — marcar/desfazer conclusão DIRETO na visão (sem
                      entrar em edição). Ação concluída não dispara mais alertas de
                      atraso (cron pula concluida_em). Save limpo de 1 campo. */}
                  {concluidaEm ? (
                    <span className="acao-concluida-badge" style={{ marginLeft: 'auto' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>check</span>
                      Concluída em {new Date(concluidaEm + 'T00:00:00').toLocaleDateString('pt-BR')}
                      <button
                        type="button"
                        className="btn-desfazer-conclusao"
                        style={{ marginLeft: '8px' }}
                        onClick={() => handleSaveFieldV4(`acoes_corretivas.${index}.concluida_em`, '')}
                        title="Desfazer marcação de concluída (volta a receber alertas de atraso)"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '13px', verticalAlign: 'text-bottom', marginRight: '2px' }}>undo</span>
                        Desfazer
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn-marcar-concluida"
                      style={{ marginLeft: 'auto' }}
                      onClick={() => handleSaveFieldV4(`acoes_corretivas.${index}.concluida_em`, new Date().toISOString().slice(0, 10))}
                      title="Marca esta ação como concluída — para de receber os alertas de atraso (D-0/D+1)"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>check</span>
                      Marcar como concluída
                    </button>
                  )}
                </div>
                <div className="acao-display-texto">{a.descricao || '—'}</div>
                <div className="acao-display-meta">
                  {a.responsavel && (
                    <span><span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>person</span>{a.responsavel}</span>
                  )}
                  {acao.prazo_data ? (
                    <span><span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>event</span>até {new Date(acao.prazo_data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                  ) : (a.prazo && (
                    <span><span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>schedule</span>{a.prazo}</span>
                  ))}
                  {acao.responsavel_email && (
                    <span><span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>mail</span>{acao.responsavel_email}</span>
                  )}
                  {/* #241 — vertente 8D (D5): a qual cadeia a ação responde.
                      Save limpo de 1 campo (padrão concluida_em acima); enum
                      fechado no backend (ocorrencia|deteccao|sistemica|vazio). */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Vertente 8D: a qual cadeia esta ação responde (ocorrência × detecção × sistêmica). Opcional.">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>alt_route</span>
                    <select
                      value={vertenteAtual}
                      onChange={(e) => handleSaveFieldV4(`acoes_corretivas.${index}.vertente`, e.target.value)}
                      style={{ fontSize: '12px', padding: '2px 4px', border: '1px solid #e2e8f0', borderRadius: '6px', color: vertenteAtual ? '#334155' : '#94a3b8', background: '#fff' }}
                    >
                      <option value="">Vertente…</option>
                      <option value="ocorrencia">Ocorrência</option>
                      <option value="deteccao">Detecção</option>
                      <option value="sistemica">Sistêmica</option>
                    </select>
                  </span>
                </div>
              </div>
              );
            })
          )
        )}
        {/* Sessão 24 #59 — datalist nativo HTML pra autocomplete do
            responsavel_email. Combina emails já usados nas próprias ações
            da NC + destinatários da conta (Account Settings). Dedup +
            lowercase. Browser sugere ao digitar. */}
        {acoesEdit && (() => {
          const emailsAcoes = (editedData.acoes_corretivas || [])
            .map((a) => (a.responsavel_email || '').trim().toLowerCase())
            .filter((e) => e && e.includes('@'));
          const allEmails = Array.from(new Set([
            ...emailsAcoes,
            ...(contaDestinatarios || []).map((e) => e.trim().toLowerCase()),
          ])).filter(Boolean);
          return (
            <datalist id="acao-emails-sugeridos">
              {allEmails.map((email) => (
                <option key={email} value={email} />
              ))}
            </datalist>
          );
        })()}
        {acoesEdit && editedData.acoes_corretivas.map((acao, index) => (
          <div key={index} className="acao-item">
            <div className="acao-header">
              <span>
                Ação {index + 1}
                {!acaoCorretivaEditada(acao, index) && (
                  <span
                    className="acao-badge-ia"
                    title="Ainda é a sugestão original da IA — não foi revisada/editada por uma pessoa. Revise após uma reanálise."
                  >
                    não editada
                  </span>
                )}
              </span>
              <button
                className="btn-remove"
                onClick={() => removeAcao(index)}
                title="Remover ação"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
              </button>
            </div>
            <input
              type="text"
              placeholder="Descrição da ação..."
              value={acao.acao}
              onChange={(e) => handleAcaoChange(index, 'acao', e.target.value)}
            />
            <input
              type="text"
              placeholder="Responsável..."
              value={acao.responsavel}
              onChange={(e) => handleAcaoChange(index, 'responsavel', e.target.value)}
            />
            {/* #66 Felipe N2.3 — prazo: dois inputs coexistem.
                prazo_dias (legado, relativo) OU prazo_data (absoluto, Felipe
                pediu data fixa). Backend `_resolve_data_limite` prioriza
                prazo_data quando preenchido. */}
            <div className="acao-prazo-row">
              <div className="acao-prazo-input">
                <label className="acao-prazo-label">Prazo (dias):</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  placeholder="ex: 30"
                  value={acao.prazo_dias ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      handleAcaoChange(index, 'prazo_dias', null);
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    if (Number.isNaN(parsed)) return;
                    const clamped = Math.max(1, Math.min(365, parsed));
                    handleAcaoChange(index, 'prazo_dias', clamped);
                  }}
                />
              </div>
              <span className="acao-prazo-ou">ou</span>
              <div className="acao-prazo-input">
                <label className="acao-prazo-label">Data limite:</label>
                <input
                  type="date"
                  value={acao.prazo_data || ''}
                  onChange={(e) =>
                    handleAcaoChange(index, 'prazo_data', e.target.value || null)
                  }
                />
              </div>
            </div>
            {/* Display data computada — quando user preenche prazo_dias mas
                não prazo_data, mostra quando vai vencer (ajuda visualizar
                quando D-3 dispara). Usa nc.created_at como âncora (espelha
                backend `data_base = nc.created_at`). */}
            {!acao.prazo_data
              && typeof acao.prazo_dias === 'number'
              && acao.prazo_dias > 0
              && (() => {
                const ancora = nc?.created_at ? new Date(nc.created_at) : new Date();
                const venc = new Date(ancora);
                venc.setDate(venc.getDate() + acao.prazo_dias);
                const venceStr = venc.toLocaleDateString('pt-BR');
                return (
                  <div className="acao-prazo-computado">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '4px' }}>event</span>
                    Vence em {venceStr}
                    {' '}
                    <small>({acao.prazo_dias} dias a partir de {ancora.toLocaleDateString('pt-BR')})</small>
                  </div>
                );
              })()}
            {/* Sessão 24 — Felipe F1.3: email do responsável pra receber
                alertas D-3/D-0/D+1 quando a ação se aproximar/passar do prazo.
                Opcional; backend cron lê este campo + destinatarios_alertas
                do client pra disparar. */}
            <input
              type="email"
              placeholder="Email do responsável (pra alerta automático)..."
              value={acao.responsavel_email || ''}
              onChange={(e) => handleAcaoChange(index, 'responsavel_email', e.target.value)}
              list="acao-emails-sugeridos"
            />
            {/* Aviso amarelo discreto quando tem prazo mas sem email pra
                receber alerta. Não bloqueia o save — só educa o usuário. */}
            {((typeof acao.prazo_dias === 'number' && acao.prazo_dias > 0) || acao.prazo_data)
              && !(acao.responsavel_email && acao.responsavel_email.includes('@')) && (
              <div className="acao-aviso-sem-email">
                <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '4px' }}>warning</span>
                Sem alerta automático — adicione o e-mail do responsável pra receber lembrete antes do prazo (D-3), no dia (D-0) e em caso de atraso (D+1).
              </div>
            )}
            {/* #66 Felipe N2.3 — status de conclusão. Ação concluída não
                dispara mais alertas D-0/D+1 (cron skip). */}
            {acao.concluida_em ? (
              <div className="acao-concluida-badge">
                <span>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>check</span>
                  Concluída em {new Date(acao.concluida_em + 'T00:00:00').toLocaleDateString('pt-BR')}
                </span>
                <button
                  type="button"
                  className="btn-desfazer-conclusao"
                  onClick={() => handleAcaoChange(index, 'concluida_em', null)}
                  title="Desfazer marcação de concluída (volta a receber alertas)"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '2px' }}>undo</span>
                  Desfazer
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-marcar-concluida"
                onClick={() => {
                  const hoje = new Date().toISOString().slice(0, 10);
                  handleAcaoChange(index, 'concluida_em', hoje);
                }}
                title="Marca esta ação como concluída — para de receber alertas D-0/D+1"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>check</span>
                Marcar como concluída
              </button>
            )}
          </div>
        ))}
        <button className="btn-add-acao" onClick={addAcao}>
          <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>add</span>
          Adicionar Ação
        </button>
      </div>

      {/* === SEÇÕES EXPANDIDAS — ANÁLISE v4.0 (Full Analysis Override) === */}
      {analiseV4 && (
        <>
          {/* (Banner + toggle de edição manual movidos pro TOPO do editor — item 6 #191) */}

          {/* Análise 6M — movida pro TOPO do editor (Investigação #193 I-A:
              spine 6M→porquês→causa→ações). Antes ficava aqui, depois das ações. */}

          {/* PR-B avulsa — campos BASE da NC (contexto/indústria/custo/impacto/
              observações) salvam via endpoints do app; sem equivalente avulsa →
              gated por allowBaseFieldEdit (default true no app, false no avulsa). */}
          {allowBaseFieldEdit && (<>
          {/* Sessão 19 — Contextos da NC (contexto_processo, contexto_setorial_custom, industria).
              Campos BASE da NC (não da análise), salvos via PUT /api/v2/nc/{id}/editar.
              Antes só editáveis no formulário de criação; agora pós-criação também.
              Sessão 21 (Decisão 4): movido pra logo após Análise 6M — antes ficava
              perdido no meio da lista após Impacto Potencial. Contexto é base da
              análise, faz sentido visualmente perto do diagnóstico Ishikawa. */}
          <div className="editor-section">
            <div className="section-header" id="editor-secao-contexto" onClick={() => toggleSection('contextos')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['contextos'] ? 'expand_more' : 'chevron_right'}</span>
                Contextos da NC
                {nc?.contexto_editado_apos_analise && stalePill}
              </h4>
            </div>
            {expandedSections['contextos'] && (
              <div className="section-content">
                {/* Nível 1 (Auditoria Sessão 44) — chip de dessincronia do INPUT:
                    o contexto foi editado depois da análise; os porquês/causa podem
                    não refletir o novo contexto. Reusa nc.contexto_editado_apos_analise
                    (some quando a cadeia é re-derivada por recálculo). Sem IA. */}
                {nc?.contexto_editado_apos_analise && (
                  <div style={{ marginBottom: '10px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>info</span>
                    <span>O contexto foi editado — a análise (6M/porquês/causa) <strong>pode não refletir o novo contexto</strong>.{(process.env.REACT_APP_CASCATA_IA_HABILITADA || 'false').toLowerCase() === 'true' && ' Use "Recalcular análise com IA" no topo da NC para re-derivar toda a análise a partir do contexto atual.'}</span>
                  </div>
                )}
                <AnalysisFieldEditor
                  label="Situação atual do processo"
                  campo="contexto_processo"
                  valorAtual={nc?.contexto_processo}
                  tipo="textarea"
                  rows={4}
                  onSave={handleSaveContextoNC} onEditingChange={onFieldEditingChange}
                  editadoPorHumano={false}
                  disabled={false}
                  placeholder="Ex: linha de extrusão 3, borracha EPDM lote 2026-03, vulcanização 180°C por 8 min, turno da tarde, operador com 2 meses de experiência..."
                />
                <AnalysisFieldEditor
                  label="Contexto Setorial / Aplicação no Cliente"
                  campo="contexto_setorial_custom"
                  valorAtual={nc?.contexto_setorial_custom}
                  tipo="textarea"
                  rows={3}
                  onSave={handleSaveContextoNC} onEditingChange={onFieldEditingChange}
                  editadoPorHumano={false}
                  disabled={false}
                  placeholder="Ex: As rolhas servem como vedação de detonadores (explosivos). Sua falha pode ocasionar perigo para retirada dos mesmos."
                />
                <AnalysisFieldEditor
                  label="Indústria"
                  campo="industria"
                  valorAtual={nc?.industria}
                  tipo="text"
                  onSave={handleSaveContextoNC} onEditingChange={onFieldEditingChange}
                  editadoPorHumano={false}
                  disabled={false}
                  placeholder="Ex: borracha, automotivo, construção civil..."
                />
              </div>
            )}
          </div>

          {/* #51 (Sessão 32) — Custo estimado da NC em R$ (eixo financeiro / Dashboard).
              Campo MANUAL (sugestão IA = #51.v2). Input texto com máscara BR:
              exibe "1.500,50", parseia pra decimal "1500.50" antes de gravar via
              o mesmo /editar dos contextos. Sempre visível (não esconder = não vira
              feature invisível). */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('custo')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['custo'] ? 'expand_more' : 'chevron_right'}</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom', marginRight: '4px' }}>payments</span>
                Custo estimado
              </h4>
            </div>
            {expandedSections['custo'] && (
              <div className="section-content">
                <AnalysisFieldEditor
                  label="Custo estimado (R$)"
                  campo="custo_estimado"
                  valorAtual={formatCurrencyBR(nc?.custo_estimado)}
                  tipo="text"
                  onSave={(campo, valor) => handleSaveContextoNC(campo, parseCurrencyBR(valor))}
                  mostrarBadge={false}
                  disabled={false}
                  placeholder="Ex: 1.500,50 (deixe vazio se não estimado)"
                />
              </div>
            )}
          </div>

          {/* #108 — Categoria de impacto (MULTI-seleção manual; eixo do Dashboard #52).
              SEM IA (espelha o custo). Salva direto via /editar (lista). */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('impacto')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['impacto'] ? 'expand_more' : 'chevron_right'}</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom', marginRight: '4px' }}>category</span>
                Categoria de impacto
              </h4>
            </div>
            {expandedSections['impacto'] && (
              <div className="section-content">
                <p className="nc-form-help" style={{ marginTop: 0 }}>
                  Marque a(s) dimensão(ões) de impacto desta NC. Alimenta o Dashboard. Pode marcar mais de uma.
                </p>
                {[
                  { value: 'financeiro', label: 'Financeiro' },
                  { value: 'reputacional', label: 'Reputacional' },
                  { value: 'operacional', label: 'Operacional' },
                ].map((opt) => {
                  const atuais = Array.isArray(nc?.categoria_impacto) ? nc.categoria_impacto : [];
                  const checked = atuais.includes(opt.value);
                  return (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const base = Array.isArray(nc?.categoria_impacto) ? [...nc.categoria_impacto] : [];
                          const novo = e.target.checked
                            ? [...new Set([...base, opt.value])]
                            : base.filter((c) => c !== opt.value);
                          handleSaveContextoNC('categoria_impacto', novo);
                        }}
                        style={{ width: 'auto' }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* #279/#280 — Tratamento da saída não conforme (ISO 8.7) + decisão
              correção × ação corretiva (ISO 10.2) + abrangência. Campos-BASE da
              NC (tabela ncs), editados pós-criação via /editar (mesmo handler do
              custo/categoria — SEM IA). Vale pros 2 modos (Registro/Investigação):
              o controle da saída não conforme independe da profundidade da análise. */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('disposicao')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['disposicao'] ? 'expand_more' : 'chevron_right'}</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom', marginRight: '4px' }}>inventory_2</span>
                Tratamento da saída não conforme (ISO 8.7)
              </h4>
            </div>
            {expandedSections['disposicao'] && (
              <div className="section-content">
                <p className="nc-form-help" style={{ marginTop: 0 }}>
                  Como a saída não conforme foi tratada e se, além de corrigir o item, é preciso eliminar a causa. Opcional — preencha o que se aplica.
                </p>

                {/* #279 — Disposição (correção/segregação/devolução/concessão) */}
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', margin: '12px 0 4px' }}>
                  Disposição da saída não conforme
                </label>
                <select
                  value={nc?.disposicao_saida || ''}
                  onChange={(e) => handleSaveContextoNC('disposicao_saida', e.target.value || null)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border, #d0d0d0)' }}
                >
                  <option value="">— Não definida —</option>
                  {DISPOSICAO_OPCOES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {/* #280 — Correção × ação corretiva (a decisão central do 10.2) */}
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', margin: '16px 0 4px' }}>
                  Requer ação corretiva?
                </label>
                <p className="nc-form-help" style={{ margin: '0 0 4px' }}>
                  A disposição acima <em>corrige</em> esta saída. A ação corretiva vai além: elimina a <strong>causa</strong> para não repetir (ISO 10.2).
                </p>
                <select
                  value={nc?.requer_acao_corretiva || ''}
                  onChange={(e) => handleSaveContextoNC('requer_acao_corretiva', e.target.value || null)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border, #d0d0d0)' }}
                >
                  <option value="">— Não definido —</option>
                  <option value="sim">Sim — investigar causa e definir ação corretiva</option>
                  <option value="nao">Não — apenas correção da saída foi suficiente</option>
                </select>

                {/* #280 — Abrangência (outros itens/processos/lotes com a mesma causa) */}
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', margin: '16px 0 4px' }}>
                  Abrangência verificada?
                </label>
                <p className="nc-form-help" style={{ margin: '0 0 4px' }}>
                  Outros produtos, processos, lotes ou clientes afetados pela mesma causa foram avaliados?
                </p>
                <select
                  value={nc?.abrangencia_verificada || ''}
                  onChange={(e) => handleSaveContextoNC('abrangencia_verificada', e.target.value || null)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border, #d0d0d0)' }}
                >
                  <option value="">— Não definido —</option>
                  <option value="sim">Sim — abrangência avaliada</option>
                  <option value="nao">Não — ainda não avaliada</option>
                </select>
              </div>
            )}
          </div>

          {/* #77 Felipe (Sessão 27) — Observações livres.
              Notas internas do usuário que NÃO entram no relatório formal
              e NÃO são consumidas pela IA. Separado visualmente dos campos
              de contexto (que alimentam o prompt). */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('observacoes')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['observacoes'] ? 'expand_more' : 'chevron_right'}</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom', marginRight: '4px' }}>edit_note</span>
                Observações
              </h4>
            </div>
            {expandedSections['observacoes'] && (
              <div className="section-content">
                <p className="nc-form-help" style={{ marginTop: 0 }}>
                  Notas internas livres. Lembretes, follow-ups, contexto que
                  não entra no relatório formal nem é usado pela IA. Visível
                  apenas pra você e sua equipe.
                </p>
                <textarea
                  className="nc-form-input"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex: Falar com cliente sobre próxima auditoria. Verificar se fornecedor X ajustou o processo após PR 5."
                  rows={5}
                  maxLength={5000}
                  disabled={savingObservacoes}
                  style={{ width: '100%', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                  <Button
                    type="button"
                    size="sm"
                    icon={savingObservacoes ? undefined : 'save'}
                    onClick={handleSaveObservacoes}
                    disabled={savingObservacoes || !observacoesDirty}
                    title={!observacoesDirty ? 'Nenhuma mudança pra salvar' : 'Salvar observações'}
                  >
                    {savingObservacoes ? 'Salvando…' : 'Salvar observações'}
                  </Button>
                  {observacoesDirty && !savingObservacoes && (
                    <span style={{ fontSize: '12px', color: '#ca8a04' }}>
                      ● Alterações não salvas
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#94a3b8' }}>
                    {observacoes.length}/5000
                  </span>
                </div>
              </div>
            )}
          </div>
          </>)}

          {/* #71 — a seção de Contenção foi MOVIDA pra antes do 6M (fluxo 8D). */}

          {/* Hipótese Alternativa — Sessão 41: oculto no tier Registro */}
          {!isRegistro && (
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('hipotese')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['hipotese'] ? 'expand_more' : 'chevron_right'}</span>
                Hipótese Alternativa
                {analiseV4?.metadados_ia?.hipotese_desalinhada && stalePill}
              </h4>
            </div>
            {expandedSections['hipotese'] && (
              <div className="section-content">
                {/* Nível 1 (Auditoria Sessão 44) — chip de staleness: a hipótese
                    editada à mão NÃO é sobrescrita pelo recalc, mas pode ter ficado
                    desalinhada da nova causa. Determinístico, sem IA. */}
                {analiseV4?.metadados_ia?.hipotese_desalinhada && (
                  <div style={{ marginBottom: '10px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>info</span>
                    <span>A causa raiz foi recalculada — esta hipótese (editada à mão) <strong>pode não refletir a análise atual</strong>. Revise se necessário.</span>
                  </div>
                )}
                <AnalysisFieldEditor label="Declaração" campo="hipotese_alternativa.declaracao" valorAtual={analiseV4?.hipotese_alternativa?.declaracao} tipo="textarea" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('hipotese_alternativa.declaracao')} disabled={false} />
                <AnalysisFieldEditor label="Justificativa de não seleção" campo="hipotese_alternativa.por_que_nao_selecionada" valorAtual={analiseV4?.hipotese_alternativa?.por_que_nao_selecionada} tipo="textarea" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('hipotese_alternativa.por_que_nao_selecionada')} disabled={false} />
                {/* #219 Fase 2 — leitura migrada do bloco read-only: cadeia de
                    porquês da hipótese (não-editável; gerada pela IA). */}
                {analiseV4?.hipotese_alternativa?.porques?.length > 0 && (
                  <div className="v4-hipotese-porques" style={{ marginTop: '8px' }}>
                    <strong>Cadeia de Porquês da hipótese:</strong>
                    <ol>
                      {analiseV4.hipotese_alternativa.porques.map((p, idx) => (
                        <li key={idx}>{formatHipoteseAlternativaPorque(p)}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {/* J1 — hipótese ORIGINAL (snapshot pré-reanálise), leitura. Só
                    quando não há hipótese atual e o snapshot existe. */}
                {!analiseV4?.hipotese_alternativa?.declaracao && analiseV4?.hipotese_alternativa_original?.declaracao && (
                  <div style={{ marginTop: '10px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Hipótese original (antes da reanálise)
                    </div>
                    <p style={{ margin: 0, fontSize: '13px' }}>{analiseV4.hipotese_alternativa_original.declaracao}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Conclusão Executiva */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('conclusao')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['conclusao'] ? 'expand_more' : 'chevron_right'}</span>
                Conclusão Executiva
                {analiseV4?.metadados_ia?.conclusao_desatualizada && stalePill}
              </h4>
            </div>
            {expandedSections['conclusao'] && (
              <div className="section-content">
                {/* Nível 1 (Auditoria Sessão 44) — chip de staleness: a causa mudou
                    e o recalc NÃO regenera a conclusão → pode estar velha. */}
                {analiseV4?.metadados_ia?.conclusao_desatualizada && (
                  <div style={{ marginBottom: '10px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>info</span>
                    <span>A causa raiz mudou — esta conclusão <strong>pode não refletir a análise atual</strong>. Revise.</span>
                  </div>
                )}
                <AnalysisFieldEditor label="Conclusão" campo="conclusao_executiva" valorAtual={analiseV4?.conclusao_executiva} tipo="textarea" rows={4} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('conclusao_executiva')} disabled={false} />
              </div>
            )}
          </div>

          {/* Impacto Potencial, 5W2H, Ações Preventivas e Verificação de Eficácia —
              Sessão 41: ocultos no tier Registro (perks da Investigação). */}
          {!isRegistro && (<>
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('impacto')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['impacto'] ? 'expand_more' : 'chevron_right'}</span>
                Impacto Potencial
                {analiseV4?.metadados_ia?.impacto_desatualizado && stalePill}
              </h4>
            </div>
            {expandedSections['impacto'] && (
              <div className="section-content">
                {/* Nível 1 (Auditoria Sessão 44) — chip de staleness: a causa mudou
                    e o recalc NÃO regenera o impacto → pode estar velho. */}
                {analiseV4?.metadados_ia?.impacto_desatualizado && (
                  <div style={{ marginBottom: '10px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>info</span>
                    <span>A causa raiz mudou — este impacto <strong>pode não refletir a análise atual</strong>. Revise.</span>
                  </div>
                )}
                <AnalysisFieldEditor label="No Cliente" campo="impacto_potencial.no_cliente" valorAtual={analiseV4?.impacto_potencial?.no_cliente} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('impacto_potencial.no_cliente')} disabled={false} />
                <AnalysisFieldEditor label="No Processo" campo="impacto_potencial.no_processo" valorAtual={analiseV4?.impacto_potencial?.no_processo} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('impacto_potencial.no_processo')} disabled={false} />
                <AnalysisFieldEditor label="Financeiro Estimado" campo="impacto_potencial.financeiro_estimado" valorAtual={analiseV4?.impacto_potencial?.financeiro_estimado} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('impacto_potencial.financeiro_estimado')} disabled={false} />
                <AnalysisFieldEditor label="Risco de Recorrência" campo="impacto_potencial.risco_recorrencia" valorAtual={analiseV4?.impacto_potencial?.risco_recorrencia} tipo="select" opcoes={['Alto', 'Médio', 'Baixo']} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('impacto_potencial.risco_recorrencia')} disabled={false} />
                <AnalysisFieldEditor label="Justificativa do Risco" campo="impacto_potencial.justificativa_risco" valorAtual={analiseV4?.impacto_potencial?.justificativa_risco} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('impacto_potencial.justificativa_risco')} disabled={false} />
              </div>
            )}
          </div>

          {/* Sessão 41 — Plano de Ação 5W2H POR AÇÃO.
              ANTES: editava um único dict consolidado `plano_5w2h.*` que a IA
              NÃO gera (não existe no schema v4.2) e que o upgrade
              Registro→Investigação não remontava → os 7 campos vinham em
              branco, e mesmo quando montados não batiam com a lista por-ação
              que o modal/PDF mostram (report_service_v2 monta uma 5W2H por
              ação corretiva). AGORA: edita o 5W2H de CADA ação corretiva via
              dot-path `acoes_corretivas.{i}.*` (whitelist já aceita — save
              imediato por handleSaveFieldV4). What/Who/When NÃO entram aqui:
              são a Descrição/Responsável/Prazo da seção "Ações Corretivas"
              acima (fonte única — evita conflito com o save em lote, que
              re-emite acoes_corretivas.{i}.what/who a cada "Salvar"). Aqui
              ficam Why/Where/How/How Much, que hoje não são editáveis em
              nenhum lugar. */}
          <div className="editor-section">
            <div className="section-header" id="editor-secao-acoes" onClick={() => toggleSection('5w2h')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['5w2h'] ? 'expand_more' : 'chevron_right'}</span>
                Plano de Ação 5W2H (por ação)
              </h4>
            </div>
            {expandedSections['5w2h'] && (
              <div className="section-content">
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>
                  O quê / Quem / Quando de cada ação são a <strong>Descrição / Responsável / Prazo</strong> da seção "Ações Corretivas" acima. Aqui você detalha <strong>Por quê / Onde / Como / Quanto</strong> de cada ação.
                </p>
                {(analiseV4?.acoes_corretivas || []).length === 0 && (
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>Nenhuma ação corretiva para detalhar.</p>
                )}
                {(analiseV4?.acoes_corretivas || []).map((acao, idx) => {
                  const titulo = acao?.what || acao?.descricao || acao?.acao || '';
                  return (
                    <div key={acao?.id || idx} className="acao-item">
                      <div className="acao-header">
                        <span><strong>Ação {idx + 1}</strong>{titulo ? `: ${titulo}` : ''}</span>
                      </div>
                      <AnalysisFieldEditor label="Why (Por quê)" campo={`acoes_corretivas.${idx}.why`} valorAtual={acao?.why} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isCampoAcaoEditado('acoes_corretivas', acao, idx, 'why')} disabled={false} placeholder="Por que esta ação ataca a causa raiz..." />
                      <AnalysisFieldEditor label="Where (Onde)" campo={`acoes_corretivas.${idx}.where`} valorAtual={acao?.where} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isCampoAcaoEditado('acoes_corretivas', acao, idx, 'where')} disabled={false} placeholder="Local onde a ação acontece..." />
                      <AnalysisFieldEditor label="How (Como)" campo={`acoes_corretivas.${idx}.how`} valorAtual={acao?.how} tipo="textarea" rows={3} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isCampoAcaoEditado('acoes_corretivas', acao, idx, 'how')} disabled={false} placeholder="Passo a passo da execução desta ação..." />
                      <AnalysisFieldEditor label="How Much (Quanto)" campo={`acoes_corretivas.${idx}.how_much`} valorAtual={acao?.how_much} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isCampoAcaoEditado('acoes_corretivas', acao, idx, 'how_much')} disabled={false} placeholder='Ex: "R$ 12.000 + 80h engenharia"' />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sessão 19 — Ações Preventivas (array com CRUD).
              Backend já aceita append implícito via `_ARRAYS_APPEND_IMPLICITO`
              em analysis_field_validator.py. Antes só apareciam no PDF e no
              modal v2.0 (read-only). Agora editáveis aqui. */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('preventivas')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['preventivas'] ? 'expand_more' : 'chevron_right'}</span>
                Ações Preventivas
              </h4>
            </div>
            {expandedSections['preventivas'] && (
              <div className="section-content">
                {(analiseV4?.acoes_preventivas || []).map((prev, idx) => (
                  <div key={prev?.id || idx} className="acao-item">
                    <div className="acao-header">
                      <span><strong>Preventiva {idx + 1}</strong>{prev?.id ? ` (${prev.id})` : ''}</span>
                      <button
                        className="btn-remove"
                        onClick={() => handleRemovePreventiva(idx)}
                        disabled={false}
                        title="Remover ação preventiva"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                      </button>
                    </div>
                    <AnalysisFieldEditor label="Descrição" campo={`acoes_preventivas.${idx}.descricao`} valorAtual={prev?.descricao} tipo="textarea" rows={2} onSave={(c, v) => handleSavePreventivaField(idx, 'descricao', v)} editadoPorHumano={isCampoAcaoEditado('acoes_preventivas', prev, idx, 'descricao')} disabled={false} placeholder="O que fazer preventivamente..." />
                    <AnalysisFieldEditor label="Abrangência" campo={`acoes_preventivas.${idx}.abrangencia`} valorAtual={prev?.abrangencia} tipo="text" onSave={(c, v) => handleSavePreventivaField(idx, 'abrangencia', v)} editadoPorHumano={isCampoAcaoEditado('acoes_preventivas', prev, idx, 'abrangencia')} disabled={false} placeholder="Ex: Linha 2 + extensível a Linha 3" />
                    <AnalysisFieldEditor label="Responsável (função)" campo={`acoes_preventivas.${idx}.responsavel_funcao`} valorAtual={prev?.responsavel_funcao} tipo="text" onSave={(c, v) => handleSavePreventivaField(idx, 'responsavel_funcao', v)} editadoPorHumano={isCampoAcaoEditado('acoes_preventivas', prev, idx, 'responsavel_funcao')} disabled={false} placeholder="Ex: Engenheiro de Processos" />
                    <AnalysisFieldEditor label="Prazo" campo={`acoes_preventivas.${idx}.prazo`} valorAtual={prev?.prazo} tipo="text" onSave={(c, v) => handleSavePreventivaField(idx, 'prazo', v)} editadoPorHumano={isCampoAcaoEditado('acoes_preventivas', prev, idx, 'prazo')} disabled={false} placeholder='Ex: "90 dias"' />
                  </div>
                ))}
                <button
                  className="btn-add-acao"
                  onClick={handleAddPreventiva}
                  disabled={false}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>add</span>
                  Adicionar Ação Preventiva
                </button>
              </div>
            )}
          </div>

          {/* Sessão 19 — Verificação de Eficácia.
              Existia no schema desde sempre e aparece no PDF, mas o NCEditor
              nunca expôs UI pra preencher. Gap apontado no caso Elastobras/
              Orica. Backend aceita dot notation `verificacao_eficacia.*`. */}
          <div className="editor-section">
            <div className="section-header" id="editor-secao-eficacia" onClick={() => toggleSection('eficacia')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['eficacia'] ? 'expand_more' : 'chevron_right'}</span>
                Verificação de Eficácia
              </h4>
            </div>
            {expandedSections['eficacia'] && (
              <div className="section-content">
                <AnalysisFieldEditor label="Critério" campo="verificacao_eficacia.criterio" valorAtual={analiseV4?.verificacao_eficacia?.criterio} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('verificacao_eficacia.criterio')} disabled={false} placeholder="Critério OBJETIVO e mensurável (o que medir)..." ajuda="O que você vai medir para saber se a ação resolveu. Ex: número de reclamações do cliente; refugo no processo." />
                <AnalysisFieldEditor label="Método" campo="verificacao_eficacia.metodo" valorAtual={analiseV4?.verificacao_eficacia?.metodo} tipo="textarea" rows={2} onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('verificacao_eficacia.metodo')} disabled={false} placeholder="Como vai medir (auditorias, CRM, CEP, etc)..." ajuda="Como você vai medir. Ex: conferir o CRM de reclamações; auditoria no posto de trabalho; inspeção dos próximos 5 lotes." />
                <AnalysisFieldEditor label="Meta" campo="verificacao_eficacia.meta" valorAtual={analiseV4?.verificacao_eficacia?.meta} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('verificacao_eficacia.meta')} disabled={false} placeholder='Ex: "Zero reclamações em 90 dias" (Cpk só se você já usa controle estatístico)' ajuda="O número que prova que deu certo. Ex: zero reclamações em 90 dias. (Cpk só se você já usa controle estatístico de processo — caso contrário, use uma meta simples.)" />
                <AnalysisFieldEditor label="Prazo de Verificação" campo="verificacao_eficacia.prazo_verificacao" valorAtual={analiseV4?.verificacao_eficacia?.prazo_verificacao} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('verificacao_eficacia.prazo_verificacao')} disabled={false} placeholder='Ex: "90 dias após conclusão da última ação corretiva"' />
                <AnalysisFieldEditor label="Responsável (função)" campo="verificacao_eficacia.responsavel_funcao" valorAtual={analiseV4?.verificacao_eficacia?.responsavel_funcao} tipo="text" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('verificacao_eficacia.responsavel_funcao')} disabled={false} placeholder="Ex: Gerente da Qualidade" />
              </div>
            )}
          </div>
          </>)}

          {/* Sessão 19 — Validação e Status.
              Os 3 checkboxes (revisado_equipe, ajustes_realizados,
              aprovado_execucao) existem no JSON desde sempre (ver
              ai_service.py:790-792), aparecem no PDF, mas nunca foram
              expostos pra edição via UI — só pra uso "físico" no impresso.
              Agora ficam interativos. Salva via PUT /editar-analise. */}
          <div className="editor-section">
            <div className="section-header" onClick={() => toggleSection('validacao')}>
              <h4>
                <span className="material-symbols-outlined section-chevron">{expandedSections['validacao'] ? 'expand_more' : 'chevron_right'}</span>
                Validação e Status
              </h4>
            </div>
            {expandedSections['validacao'] && (
              <div className="section-content">
                <AnalysisFieldEditor label="Revisado pela equipe" campo="validacao.revisado_equipe" valorAtual={analiseV4?.validacao?.revisado_equipe} tipo="boolean" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('validacao.revisado_equipe')} disabled={false} />
                <AnalysisFieldEditor label="Ajustes realizados" campo="validacao.ajustes_realizados" valorAtual={analiseV4?.validacao?.ajustes_realizados} tipo="boolean" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('validacao.ajustes_realizados')} disabled={false} />
                <AnalysisFieldEditor label="Aprovado para execução" campo="validacao.aprovado_execucao" valorAtual={analiseV4?.validacao?.aprovado_execucao} tipo="boolean" onSave={handleSaveFieldV4} onEditingChange={onFieldEditingChange} editadoPorHumano={isEditadoPorHumano('validacao.aprovado_execucao')} disabled={false} />
              </div>
            )}
          </div>
        </>
      )}

      {/* #219 Fase 2 — leitura migrada do bloco read-only: padrão identificado
          pela IA (display-only; sumiria em rascunho sem isto). */}
      {analise?.padrao_identificado && (
        <div className="pattern-alert" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>warning</span>Padrão identificado: {analise.padrao_identificado}
        </div>
      )}

      <div className="editor-footer">
        <p className="editor-note">
          <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '4px' }}>lightbulb</span>
          Suas edições ficam registradas na trilha de auditoria desta NC
        </p>
      </div>
    </div>
  );
}

export default NCEditor;
